import React, { useState, useEffect, useMemo, useRef } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useSearchParams, useParams, useLocation, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Badge } from './components/ui/badge';
import { Separator } from './components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { toast, Toaster } from 'sonner';
import { ShoppingCart, User, Star, Award, Globe, Coins, DollarSign, Trash2, Plus, Minus, Settings } from 'lucide-react';
import AdminDashboard from './components/AdminDashboard';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// --- Site-settings cache -------------------------------------------------
// Persist the last successful /api/settings response so every component can
// hydrate synchronously on first render, avoiding the flash of the old
// default (orange) theme while the network request is in flight.
const SITE_SETTINGS_CACHE_KEY = 'hbe_site_settings_v1';

const readCachedSettings = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(SITE_SETTINGS_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCachedSettings = (settings) => {
  if (typeof window === 'undefined' || !settings) return;
  try {
    window.localStorage.setItem(SITE_SETTINGS_CACHE_KEY, JSON.stringify(settings));
    // Also refresh the CSS custom properties used by the pre-React bootstrap
    // so any future first paint reflects the freshest theme.
    const r = document.documentElement.style;
    if (settings.primary_color) r.setProperty('--hbe-primary', settings.primary_color);
    if (settings.secondary_color) r.setProperty('--hbe-secondary', settings.secondary_color);
    if (settings.accent_color) r.setProperty('--hbe-accent', settings.accent_color);
    if (settings.text_color) r.setProperty('--hbe-text', settings.text_color);
    if (settings.border_color) r.setProperty('--hbe-border', settings.border_color);
    if (settings.background_type && settings.background_value) {
      if (settings.background_type === 'image') {
        r.setProperty('--hbe-bg', `url('${settings.background_value}') center/cover no-repeat fixed`);
      } else {
        r.setProperty('--hbe-bg', settings.background_value);
      }
    }
  } catch { /* ignore */ }
};

// --- Products cache ------------------------------------------------------
// Keeps the last successful /api/products response per category in
// localStorage so category clicks (and page reloads) can render products
// immediately from cache while the network refresh happens in the
// background — no more empty grids between transitions.
const PRODUCTS_CACHE_KEY = 'hbe_products_cache_v1';

const readCachedProducts = (category) => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(PRODUCTS_CACHE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw) || {};
    return obj[category || '__all__'] || null;
  } catch {
    return null;
  }
};

const writeCachedProducts = (category, data) => {
  if (typeof window === 'undefined' || !Array.isArray(data)) return;
  try {
    const raw = window.localStorage.getItem(PRODUCTS_CACHE_KEY);
    const obj = raw ? JSON.parse(raw) : {};
    obj[category || '__all__'] = data;
    window.localStorage.setItem(PRODUCTS_CACHE_KEY, JSON.stringify(obj));
  } catch { /* ignore */ }
};

// --- Coin subsection filters (Silver Coins category) -------------------
// Justin (2026-10-03) asked for subsection toggles under Sort & Filter on the
// coin category page: "Canadian Silver coinage" and "Newfoundland
// Silver coinage", each with a denomination dropdown. Justin (2026-10-04)
// renamed the "Canadian Coins" category to "Silver Coins" in the admin and
// asked for a "United Kingdom Silver coinage" subsection as well (its
// denominations are still to come).
// There are no backend categories for these yet, so filtering is done
// client-side against each product's denomination/title/description
// (word-boundary keyword match). When a subsection is picked, products are
// pulled across all categories (the Newfoundland pieces live in their own
// "Newfoundland …" categories) and filtered by the subsection/denomination
// keywords. If matching backend categories (or a dedicated product field)
// are added later, this should be re-wired to filter on that instead.
const SILVER_COINS_CATEGORY = 'Silver Coins';

const COIN_SUBSECTIONS = [
  {
    id: 'canadian-silver',
    label: 'Canadian Silver coinage',
    denoms: [
      { value: 'ca-silver-dollar', label: 'Canadian silver dollars' },
      { value: 'ca-half-dollar', label: 'Silver Half Dollars' },
      { value: 'ca-quarter', label: 'Silver Quarters' },
      { value: 'ca-dime', label: 'Silver Dimes' },
    ],
  },
  {
    id: 'newfoundland-silver',
    label: 'Newfoundland Silver coinage',
    denoms: [
      { value: 'nf-gold-2dollar', label: 'Newfoundland Gold 2 Dollar pieces' },
      { value: 'nf-50cent', label: 'Newfoundland Silver 50 cent pieces' },
      { value: 'nf-25cent', label: 'Newfoundland Silver 25 cent pieces' },
      { value: 'nf-20cent', label: 'Newfoundland Silver 20 cent pieces' },
      { value: 'nf-10cent', label: 'Newfoundland 10 Cent pieces' },
      { value: 'nf-5cent', label: 'Newfoundland 5 cent pieces' },
    ],
  },
  {
    id: 'uk-silver',
    label: 'United Kingdom Silver coinage',
    denoms: [
      // Justin (2026-10-05): ordered highest to lowest by pence value
      { value: 'uk-crown', label: 'Crown (5s)' },
      { value: 'uk-double-florin', label: 'Double Florin (4s)' },
      { value: 'uk-half-crown', label: 'Half-crown (2s 6d)' },
      { value: 'uk-florin', label: 'Florin (2s)' },
      { value: 'uk-shilling', label: 'Shilling (1s)' },
    ],
  },
];

const coinHaystack = (p) =>
  `${p.denomination || ''} ${p.title || ''} ${p.description || ''}`.toLowerCase();

const notNewfoundland = (h) => !/newfoundland/.test(h);
// Banknotes (e.g. Dominion of Canada 25 Cent Bills / shinplasters) must not
// match the coin denomination filters.
const notBanknote = (h) => !/\bbills?\b/.test(h);

const COIN_DENOM_MATCH = {
  // "Silver" required on the dollar matcher so banknotes denominated in
  // dollars (e.g. Zimbabwe 1,000 Dollars) don't false-positive.
  'ca-silver-dollar': (h) => /silver/.test(h) && /\bdollars?\b/.test(h) && !/half[\s-]?dollars?/.test(h) && notNewfoundland(h) && notBanknote(h),
  // Justin's 50c/25c/10c sheets are titled "50 Cents"/"25 Cents", not "Half Dollars"/"Quarters".
  'ca-half-dollar': (h) => (/half[\s-]?dollars?/.test(h) || /\b50\s?cents?\b/.test(h)) && notNewfoundland(h) && notBanknote(h),
  'ca-quarter': (h) => (/\bquarters?\b/.test(h) || /\b25\s?cents?\b/.test(h)) && notNewfoundland(h) && notBanknote(h),
  'ca-dime': (h) => (/\bdimes?\b/.test(h) || /\b10\s?cents?\b/.test(h)) && notNewfoundland(h) && notBanknote(h),
  'nf-gold-2dollar': (h) => /newfoundland/.test(h) && /gold/.test(h) && /2[\s-]?dollars?/.test(h),
  'nf-50cent': (h) => /newfoundland/.test(h) && /\b50\s?cents?\b/.test(h),
  'nf-25cent': (h) => /newfoundland/.test(h) && /\b25\s?cents?\b/.test(h),
  'nf-20cent': (h) => /newfoundland/.test(h) && /\b20\s?cents?\b/.test(h),
  'nf-10cent': (h) => /newfoundland/.test(h) && /\b10\s?cents?\b/.test(h),
  'nf-5cent': (h) => /newfoundland/.test(h) && /\b5\s?cents?\b/.test(h),
  // UK silver coinage (Justin 2026-10-05) — pre-decimal denominations.
  // Match on the coin name; require UK context to avoid false positives.
  'uk-crown': (h) => /\bcrowns?\b/.test(h) && !/half[\s-]?crown/.test(h) && notBanknote(h),
  'uk-double-florin': (h) => /double[\s-]?florins?/.test(h) && notBanknote(h),
  'uk-half-crown': (h) => /half[\s-]?crowns?/.test(h) && notBanknote(h),
  'uk-florin': (h) => /\bflorins?\b/.test(h) && !/double[\s-]?florin/.test(h) && notBanknote(h),
  'uk-shilling': (h) => /\bshillings?\b/.test(h) && notBanknote(h),
};

// Subsection-level match: any of the subsection's denomination matchers.
// Used so picking a subsection (without a denomination) shows that
// subsection's coins across all categories.
const COIN_SUBSECTION_MATCH = {
  'canadian-silver': (h) =>
    ['ca-silver-dollar', 'ca-half-dollar', 'ca-quarter', 'ca-dime'].some((k) => COIN_DENOM_MATCH[k](h)),
  'newfoundland-silver': (h) =>
    ['nf-gold-2dollar', 'nf-50cent', 'nf-25cent', 'nf-20cent', 'nf-10cent', 'nf-5cent'].some((k) => COIN_DENOM_MATCH[k](h)),
  // UK denominations from Justin (2026-10-05).
  'uk-silver': (h) =>
    ['uk-crown', 'uk-double-florin', 'uk-half-crown', 'uk-florin', 'uk-shilling'].some((k) => COIN_DENOM_MATCH[k](h)),
};

// --- ProductImage --------------------------------------------------------
// Renders a lazy-loaded image with a shimmering skeleton underneath until
// the source finishes decoding. Falls back to a placeholder on error so the
// grid never shows a broken icon.
const FALLBACK_PRODUCT_IMG = 'https://images.unsplash.com/photo-1580519542036-c47de6196ba5?w=600&q=70';

// Thumbnail variant: the backend serves a small cached WebP when ?thumb=1
// is appended to an /api/files/… image URL. Grids and carts use this;
// detail/zoom views keep the full-size original.
function thumbUrl(url) {
  if (typeof url !== 'string' || !url.startsWith('/api/files/')) return url;
  return url + (url.includes('?') ? '&' : '?') + 'thumb=1';
}

function ProductImage({ src, alt, className, testId, onClick, skeletonClassName, thumb }) {
  const [loaded, setLoaded] = React.useState(false);
  // Prefix relative URLs (e.g. /api/files/…) with BACKEND_URL so mobile
  // clients and future SSR/embed contexts always load from the same origin.
  const effectiveSrc = thumb ? thumbUrl(src) : src;
  const resolvedSrc = effectiveSrc && typeof effectiveSrc === 'string' && effectiveSrc.startsWith('/')
    ? `${BACKEND_URL}${effectiveSrc}`
    : effectiveSrc;
  React.useEffect(() => { setLoaded(false); }, [resolvedSrc]);
  return (
    <>
      {!loaded && (
        <div
          className={`absolute inset-0 img-skeleton ${skeletonClassName || ''}`}
          aria-hidden="true"
        />
      )}
      <img
        src={resolvedSrc}
        alt={alt}
        className={className}
        loading="lazy"
        decoding="async"
        data-testid={testId}
        onClick={onClick}
        onLoad={() => setLoaded(true)}
        onError={(e) => {
          setLoaded(true);
          if (e.target.src !== FALLBACK_PRODUCT_IMG) {
            e.target.onerror = null;
            e.target.src = FALLBACK_PRODUCT_IMG;
          }
        }}
      />
    </>
  );
}

// Auth Context
const AuthContext = React.createContext();

function AuthProvider({ children }) {
  // Hydrate synchronously from localStorage on the very first render so
  // <RequireAdmin> and other guards don't briefly see a null user on refresh.
  const _initialToken = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const _initialUser = (() => {
    if (typeof window === 'undefined') return null;
    try { return JSON.parse(localStorage.getItem('user') || 'null'); }
    catch { return null; }
  })();
  if (_initialToken && typeof window !== 'undefined') {
    axios.defaults.headers.common['Authorization'] = `Bearer ${_initialToken}`;
  }
  const [user, setUser] = useState(_initialUser);
  const [token, setToken] = useState(_initialToken);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // If the backend rejects our token (401), drop the stale session instead of
  // leaving the user on screens whose API calls can never succeed.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          setToken(null);
          setUser(null);
          try {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          } catch (_) {}
          delete axios.defaults.headers.common['Authorization'];
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const login = (tokenData, userData) => {
    setToken(tokenData);
    setUser(userData);
    localStorage.setItem('token', tokenData);
    localStorage.setItem('user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${tokenData}`;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

const useAuth = () => React.useContext(AuthContext);

// Currency Context — Justin 2026-10-05: live rates, round UP to nearest dollar, checkout stays CAD.
// Ryan 2026-10-05: must use the CURRENT exchange rate of the selected currency.
const CurrencyContext = React.createContext();

function CurrencyProvider({ children }) {
  const [displayCurrency, setDisplayCurrency] = useState('CAD');
  const [liveRates, setLiveRates] = useState(null);
  const FALLBACK_RATES = { CAD: 1, USD: 0.70, EUR: 0.62, GBP: 0.53, AUD: 1.01, JPY: 110.7 };
  const CURRENCY_SYMBOLS = { CAD: '$', USD: 'US$', EUR: '€', GBP: '£', AUD: 'A$', JPY: '¥' };

  useEffect(() => {
    fetch('https://open.er-api.com/v6/latest/CAD')
      .then((r) => r.json())
      .then((d) => {
        if (d && d.result === 'success' && d.rates) {
          setLiveRates({ CAD: 1, USD: d.rates.USD, EUR: d.rates.EUR, GBP: d.rates.GBP, AUD: d.rates.AUD, JPY: d.rates.JPY });
        }
      })
      .catch(() => { /* FALLBACK_RATES used instead */ });
  }, []);

  const formatDisplayPrice = (cadPrice) => {
    const rates = liveRates || FALLBACK_RATES;
    const rate = rates[displayCurrency] || 1;
    const symbol = CURRENCY_SYMBOLS[displayCurrency] || '$';
    const rawConverted = (parseFloat(cadPrice) || 0) * rate;
    // Sub-dollar amounts must show their exact cents — rounding them UP to
    // $1 (Justin 2026-10-05 rule, below) turned the 1-cent test listings
    // into $1 items and a $0.03 cart total into $1 (Ryan, 2026-11-03).
    if (rawConverted !== 0 && Math.abs(rawConverted) < 1) {
      return `${symbol}${rawConverted.toFixed(2)}`;
    }
    // Justin: round non-whole amounts UP to the nearest dollar.
    const converted = Math.ceil(rawConverted);
    return `${symbol}${converted}`;
  };

  return (
    <CurrencyContext.Provider value={{ displayCurrency, setDisplayCurrency, formatDisplayPrice, currencyCodes: Object.keys(FALLBACK_RATES) }}>
      {children}
    </CurrencyContext.Provider>
  );
}

const useCurrency = () => React.useContext(CurrencyContext);

// Spot Price Ticker — Justin 2026-10-05: live gold/silver prices, green when up / red when down (Kitco-style).
// Prices from gold-api.com (free, no key); direction vs last seen price stored in localStorage; refreshes every 60s.
function SpotPriceTicker({ forceShow = false }) {
  // Ryan 2026-10-05: show last known price instantly from storage, refresh in background
  const [spots, setSpots] = useState(() => {
    try {
      const xau = parseFloat(localStorage.getItem('hbe-spot-XAU') || 'NaN');
      const xag = parseFloat(localStorage.getItem('hbe-spot-XAG') || 'NaN');
      return { XAU: isNaN(xau) ? null : xau, XAG: isNaN(xag) ? null : xag };
    } catch (_) { return { XAU: null, XAG: null }; }
  });
  const [dirs, setDirs] = useState({ XAU: 0, XAG: 0 });

  const fetchSpots = async () => {
    try {
      const timeout = (ms) => new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), ms));
      // Justin 2026-10-06: use backend proxy (browsers were failing to reach gold-api.com directly)
      const data = await Promise.race([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/spot-prices`).then((r) => r.json()),
        timeout(8000),
      ]).catch(() => null);
      if (!data) return;
      const next = {};
      const nextDirs = {};
      [['XAU', data.XAU], ['XAG', data.XAG]].forEach(([sym, price]) => {
        if (typeof price !== 'number') return;
        next[sym] = price;
        try {
          const prev = parseFloat(localStorage.getItem(`hbe-spot-${sym}`) || 'NaN');
          nextDirs[sym] = isNaN(prev) ? 0 : (price > prev ? 1 : price < prev ? -1 : 0);
          localStorage.setItem(`hbe-spot-${sym}`, String(price));
        } catch (_) { nextDirs[sym] = 0; }
      });
      if (Object.keys(next).length) {
        setSpots((s) => ({ ...s, ...next }));
        setDirs((d) => ({ ...d, ...nextDirs }));
      }
    } catch (_) { /* ticker stays on last known / placeholder on failure */ }
  };

  useEffect(() => {
    fetchSpots();
    const id = setInterval(fetchSpots, 60000);
    return () => clearInterval(id);
  }, []);

  if (spots.XAU == null && spots.XAG == null) {
    // Ryan 2026-10-05: show loading placeholder instead of blank while fetching
    return (
      <div className={`${forceShow ? 'flex' : 'hidden md:flex'} items-center text-xs md:text-[15px]`} title="Loading spot prices...">
        <span style={{ color: '#1A1A1A', fontWeight: 600, marginRight: 12 }}>GOLD …</span>
        <span style={{ color: '#1A1A1A', fontWeight: 600 }}>SILVER …</span>
      </div>
    );
  }

  const item = (label, sym) => {
    if (spots[sym] == null) return null;
    const dir = dirs[sym];
    const color = dir > 0 ? '#16a34a' : dir < 0 ? '#dc2626' : '#1A1A1A';
    const arrow = dir > 0 ? '▲' : dir < 0 ? '▼' : '•';
    return (
      <span style={{ color, fontWeight: 600, marginRight: 12 }} data-testid={`spot-${sym.toLowerCase()}`}>
        {/* Justin 2026-10-19: show CAD right of the live prices */}
        {label} ${spots[sym].toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} CAD {arrow}
      </span>
    );
  };

  return (
    <div className={`${forceShow ? 'flex' : 'hidden md:flex'} items-center text-xs md:text-[15px]`} title="Live spot prices (CAD)">
      {item('GOLD', 'XAU')}
      {item('SILVER', 'XAG')}
    </div>
  );
}

// Convert a product title into a URL-friendly slug for /product/:slug routes.
const slugify = (s) =>
  (s || '')
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

// Components

/** Quin design tokens (approved preview) — prefer these over amber Trax defaults */
const HBE = {
  paper: '#F7F4EF',
  ink: '#1A1A1A',
  muted: '#5A554C',
  bronze: '#8B7355',
  stone: '#D9D2C5',
  slate: '#243140',
  slateSoft: '#E4E9EE',
  white: '#FFFFFF',
  contentMax: 1680,
  canadaRed: '#C8102E',
  // Justin 2026-10-05: lighter navy header gradient (Option B), lighter on left/right.
  navyHeader: 'linear-gradient(90deg, #5a8ac4 0%, #3a5a8a 50%, #5a8ac4 100%)',
  navyMarquee: 'linear-gradient(90deg, #5a8ac4 0%, #7aaad4 100%)',
};

/** Parse grade string (PMG/PCGS/Legacy/BCS/CCCS/WMG etc.) into comparable rank. Higher = better. */
function gradeRank(product) {
  const raw = String((product && (product.grade || product.grade_label || '')) || '').trim();
  if (!raw) return -1;
  const upper = raw.toUpperCase();
  // Prefer explicit numeric grade like PMG 65, BCS VF-30, WMG Choice VF 35
  const numMatches = upper.match(/(\d{1,3}(?:\.\d+)?)/g);
  let best = -1;
  if (numMatches) {
    for (const m of numMatches) {
      const n = parseFloat(m);
      if (Number.isFinite(n) && n >= 1 && n <= 70 && n > best) best = n;
    }
  }
  if (best >= 0) return best;
  // Qualitative fallbacks (common paper-money shorthand)
  const qualitative = [
    [/GEM\s*UNC|GEMUNC|GUNC/, 65],
    [/\bUNC\b|UNCIRCULATED/, 60],
    [/\bAU\b|ABOUT\s*UNC/, 55],
    [/\bXF\b|\bEF\b|EXTRA\s*FINE|EXTREMELY\s*FINE/, 45],
    [/\bVF\b|VERY\s*FINE/, 30],
    [/\bF\b|FINE\b/, 20],
    [/\bVG\b|VERY\s*GOOD/, 10],
    [/\bG\b|GOOD\b/, 6],
    [/\bAG\b|ABOUT\s*GOOD/, 3],
    [/\bP\b|POOR/, 1],
  ];
  for (const [re, score] of qualitative) {
    if (re.test(upper)) return score;
  }
  return 0;
}

/** Numeric year; null if missing/invalid. Also recovers year from title when field is null. */
function yearNum(product) {
  const n = parseInt(product && product.year, 10);
  if (Number.isFinite(n) && n > 1000 && n < 2100) return n;
  const title = String((product && product.title) || '');
  const m = title.match(/\b(1[7-9]\d{2}|20\d{2})\b/);
  if (m) {
    const y = parseInt(m[1], 10);
    if (Number.isFinite(y)) return y;
  }
  return null;
}

/** Extract Charlton / Friedberg / BC / Pick / Catalogue # when present in description. */
function catalogueLine(product) {
  const d = String((product && product.description) || '');
  if (!d) return null;
  const patterns = [
    /Charlton\s+[A-Z0-9][A-Z0-9.\/-]*/i,
    /Friedberg\s+[A-Z0-9][A-Z0-9.\/-]*/i,
    /\bBC-\d+[a-zA-Z]?\b/,
    /Pick\s+\d+[a-zA-Z]?/i,
    /Catalogue\s*#?\s*[A-Z0-9-]+/i,
    /catalog(?:ue)?\s*(?:number|#)\s*[A-Z0-9-]+/i,
  ];
  const hits = [];
  for (const re of patterns) {
    const m = d.match(re);
    if (m && m[0] && !hits.some((h) => h.toLowerCase() === m[0].toLowerCase())) hits.push(m[0].trim());
  }
  return hits.length ? hits.join(' · ') : null;
}


/** Small Canada flag mark — matches mobile emoji look without relying on emoji fonts */
function CanadaFlagMark({ className = "h-3.5 w-[22px]", title = "Canada" }) {
  // Real Canadian flag asset (Wikimedia Flag of Canada SVG) — not a hand-drawn leaf
  return (
    <img
      src={`${process.env.PUBLIC_URL || ''}/canada-flag.svg`}
      alt={title}
      title={title}
      className={className}
      width={22}
      height={11}
      decoding="async"
      style={{ display: "inline-block", flexShrink: 0, borderRadius: 2, boxShadow: "0 0 0 1px rgba(0,0,0,0.08)", objectFit: "cover" }}
    />
  );
}

function parseMarqueeStatements(raw) {
  const text = String(raw || '').trim();
  if (!text) return [];
  // Split on newlines and/or spaced dashes (—, –, -)
  return text
    .split(/\n+| +[—–-] +/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const DEFAULT_MARQUEE_STATEMENTS = [
  'Authenticated notes',
  'Graded by PMG / PCGS / BCS',
  'Secure checkout',
  'Canadian dealer',
  'Email Historic.Bills@gmail.com with any inquiries',
];

const DEFAULT_ANNOUNCEMENT_TEXT = DEFAULT_MARQUEE_STATEMENTS.join(' — ');

/** Bold the first word of known trust phrases; auto-link emails. */
function renderMarqueeStatement(text, key) {
  const emailRe = /([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;
  const parts = [];
  let last = 0;
  let m;
  let partKey = 0;
  while ((m = emailRe.exec(text)) !== null) {
    if (m.index > last) {
      parts.push(renderPlainWithOptionalBold(text.slice(last, m.index), `${key}-t${partKey++}`));
    }
    parts.push(
      <a key={`${key}-e${partKey++}`} href={`mailto:${m[1]}`}>{m[1]}</a>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) {
    parts.push(renderPlainWithOptionalBold(text.slice(last), `${key}-t${partKey++}`));
  }
  if (!parts.length) {
    return <span key={key}>{renderPlainWithOptionalBold(text, key)}</span>;
  }
  return <span key={key}>{parts}</span>;
}

function renderPlainWithOptionalBold(chunk, key) {
  if (!chunk) return null;
  const known = ['Authenticated', 'Graded', 'Secure', 'Canadian'];
  for (const w of known) {
    if (chunk === w || chunk.startsWith(w + ' ')) {
      return (
        <span key={key}>
          <b>{w}</b>
          {chunk.slice(w.length)}
        </span>
      );
    }
  }
  return <span key={key}>{chunk}</span>;
}

function TrustStrip({ settings }) {
  // Prefer admin Custom announcement banner; fall back to trust defaults + email line
  const enabled = settings?.announcement_enabled !== false;
  if (!enabled) return null;

  const custom = String(settings?.announcement_text || '').trim();
  const statements = custom
    ? parseMarqueeStatements(custom)
    : DEFAULT_MARQUEE_STATEMENTS;

  if (!statements.length) return null;

  const itemNodes = statements.map((s, i) => renderMarqueeStatement(s, `stmt-${i}`));

  const renderTrack = (suffix) => {
    const nodes = [];
    itemNodes.forEach((item, i) => {
      if (i > 0) {
        nodes.push(
          <span className="hbe-trust-dash" aria-hidden="true" key={`${suffix}-dash-${i}`}>—</span>
        );
      }
      nodes.push(
        <span className="hbe-trust-item" key={`${suffix}-item-${i}`}>{item}</span>
      );
    });
    nodes.push(
      <span className="hbe-trust-dash" aria-hidden="true" key={`${suffix}-dash-end`}>—</span>
    );
    return (
      <div className="hbe-trust-track" aria-hidden={suffix === 'b' ? 'true' : undefined} key={suffix}>
        {nodes}
      </div>
    );
  };

  return (
    <div className="hbe-trust-strip" data-testid="trust-strip">
      <div className="hbe-trust-marquee" role="presentation">
        {renderTrack('a')}
        {renderTrack('b')}
      </div>
    </div>
  );
}

function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cartCount, setCartCount] = useState(0);
  const { displayCurrency, setDisplayCurrency, currencyCodes } = useCurrency();
  const [siteSettings, setSiteSettings] = useState(readCachedSettings());
  const [searchQuery, setSearchQuery] = useState('');

  const fetchCartCount = async () => {
    if (user) {
      try {
        const response = await axios.get(`${API}/cart`);
        const totalItems = response.data.reduce((sum, item) => sum + item.quantity, 0);
        setCartCount(totalItems);
      } catch (error) {
        console.error('Error fetching cart:', error);
      }
    }
  };

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSiteSettings(response.data);
      writeCachedSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  useEffect(() => {
    fetchCartCount();
    fetchSiteSettings();
  }, [user]);

  const primaryColor = siteSettings?.primary_color || HBE.ink;
  const secondaryColor = siteSettings?.secondary_color || HBE.bronze;
  const borderColor = siteSettings?.border_color || HBE.stone;
  const textColor = siteSettings?.text_color || HBE.ink;
  // Justin 2026-10-05: lighter navy gradient header (Option B); admin header_color still overrides.
  const headerColor = siteSettings?.header_color || null;

  const headerStyle = headerColor
    ? { backgroundColor: headerColor, borderColor: borderColor }
    : { background: HBE.navyHeader, borderColor: borderColor };

  return (
    <>
    <header className="border-b shadow-none" style={{ ...headerStyle, borderBottomColor: borderColor, borderBottomWidth: 1 }}>
      <div className="mx-auto px-4 md:px-6 py-3.5" style={{ maxWidth: HBE.contentMax }}>
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3">
            {siteSettings?.logo_url && siteSettings.logo_url.trim() ? (
              <img 
                src={siteSettings.logo_url} 
                alt="Logo" 
                className="h-12 w-auto object-contain max-w-[200px]"
                onError={(e) => {
                  console.error('Logo failed to load:', siteSettings.logo_url);
                  e.target.style.display = 'none';
                }}
                onLoad={() => {
                  console.log('Logo loaded successfully:', siteSettings.logo_url);
                }}
              />
            ) : (
              <img 
                src={process.env.PUBLIC_URL + '/hbe-logo.png'} 
                alt="Historic Bill Exchange" 
                className="h-12 w-auto object-contain max-w-[200px]"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            <div>
              <h1 className="text-2xl font-bold font-serif" style={{ color: textColor }}>
                {siteSettings?.site_title || 'Historical Bill Exchange'}
              </h1>
              <p className="text-[10px] tracking-[0.22em] uppercase mt-1 flex items-center gap-1.5" style={{ color: '#000000' }}>
                <CanadaFlagMark className="h-3 w-[18px] md:h-3.5 md:w-[22px]" />
                <span>{siteSettings?.site_subtitle || 'Premium Collectible Currency'}</span>
              </p>
            </div>
          </Link>
          
          <nav className="hidden md:flex items-center space-x-6">
            <Link 
              to="/products" 
              className="font-medium transition-colors hover:opacity-80"
              style={{ color: textColor }}
            >
              Browse Collection
            </Link>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const q = searchQuery.trim();
                if (q) navigate(`/products?search=${encodeURIComponent(q)}`);
              }}
              className="relative"
              role="search"
            >
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search year, bank, denomination…"
                aria-label="Search products"
                data-testid="header-search-input"
                className="pl-9 pr-3 py-2 w-56 lg:w-72 rounded-full border-2 bg-white/90 text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: primaryColor, color: textColor }}
              />
              <svg
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
                fill="none"
                stroke={primaryColor}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </form>
            {user?.is_admin && (
              <Link 
                to="/admin" 
                className="font-medium transition-colors hover:opacity-80 flex items-center"
                style={{ color: textColor }}
              >
                <Settings className="h-4 w-4 inline mr-1" />
                Admin
              </Link>
            )}
          </nav>

          <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-4 flex-shrink-0">
            {user ? (
              <>
                {user.is_admin && (
                  <Link to="/admin" className="md:hidden">
                    <Button
                      variant="outline"
                      size="icon"
                      className="hover:opacity-80"
                      style={{ borderColor: borderColor, color: textColor }}
                      aria-label="Admin dashboard"
                      data-testid="mobile-admin-link"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </Link>
                )}
                {/* Justin 2026-10-05: currency switcher (display only, checkout stays CAD) */}
                <select
                  value={displayCurrency}
                  onChange={(e) => setDisplayCurrency(e.target.value)}
                  data-testid="currency-selector"
                  className="text-sm rounded border px-2 py-1 bg-white/90"
                  style={{ borderColor: borderColor, color: '#1A1A1A' }}
                  title="Display currency (checkout is in CAD)"
                >
                  {currencyCodes.map((code) => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
                <Link to="/cart" className="relative">
                  <Button 
                    variant="outline" 
                    size="icon" 
                    className="hover:opacity-80"
                    style={{ borderColor: borderColor }}
                  >
                    <ShoppingCart className="h-4 w-4" />
                    {cartCount > 0 && (
                      <Badge 
                        className="absolute -top-2 -right-2 text-white min-w-[20px] h-5 rounded-full text-xs flex items-center justify-center"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {cartCount}
                      </Badge>
                    )}
                  </Button>
                </Link>
                <div className="flex items-center space-x-2">
                  <span className="hidden sm:inline font-medium" style={{ color: textColor }}>
                    Hello, {user.name}
                    {user.is_admin && (
                      <Badge className="ml-2 text-white" style={{ backgroundColor: primaryColor }}>
                        Admin
                      </Badge>
                    )}
                  </span>
                  {/* Justin 2026-10-07: on mobile the admin gear icon pushes the header
                      logout button off the banner — hide it here for admins; it
                      renders in the mobile logout strip below the header instead. */}
                  <Button 
                    onClick={logout} 
                    variant="outline" 
                    className={`hover:opacity-80 ${user.is_admin ? 'hidden md:inline-flex' : ''}`}
                    style={{ borderColor: borderColor, color: textColor }}
                    data-testid="header-logout-btn"
                  >
                    Logout
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-2">
                <AuthDialog />
              </div>
            )}
          </div>
      </div>
    </div>
    </header>
    {/* Justin 2026-10-07: mobile admin logout strip — when the admin gear icon is
        present it crowds the logout button off the banner on small screens, so the
        logout button lives in its own tidy row below the header instead. */}
    {user?.is_admin && (
      <div className="md:hidden border-b" style={{ ...headerStyle, borderBottomColor: borderColor, borderBottomWidth: 1 }}>
        <div className="mx-auto px-4 py-2 flex justify-end" style={{ maxWidth: HBE.contentMax }}>
          <Button
            onClick={logout}
            variant="outline"
            size="sm"
            className="hover:opacity-80"
            style={{ borderColor: borderColor, color: textColor }}
            data-testid="mobile-logout-btn"
          >
            Logout
          </Button>
        </div>
      </div>
    )}
    {/* Justin 2026-10-08: mobile search bar — the header search lives in the
        desktop-only nav (hidden md:flex), so small screens get their own
        full-width search row below the header. Same query state, same target. */}
    <div className="md:hidden border-b" style={{ background: '#f7f4ef', borderBottomColor: borderColor, borderBottomWidth: 1 }}>
      <div className="mx-auto px-4 py-2" style={{ maxWidth: HBE.contentMax }}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const q = searchQuery.trim();
            if (q) navigate(`/products?search=${encodeURIComponent(q)}`);
          }}
          className="relative"
          role="search"
        >
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search year, bank, denomination…"
            aria-label="Search products"
            data-testid="mobile-header-search-input"
            className="w-full pl-9 pr-3 py-2 rounded-full border-2 bg-white/90 text-sm focus:outline-none focus:ring-2"
            style={{ borderColor: primaryColor, color: textColor }}
          />
          <svg
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
            fill="none"
            stroke={primaryColor}
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </form>
      </div>
    </div>
    <TrustStrip settings={siteSettings} />
    {/* Justin 2026-10-06: spot price bar below the moving header (all screens) */}
    <div className="flex justify-center items-center py-2 text-sm border-b" style={{ background: '#f7f4ef', borderBottomColor: '#e5e0d5' }}>
      <SpotPriceTicker forceShow />
    </div>
    </>
  );
}

function AuthDialog() {
  const { login } = useAuth();
  const [step, setStep] = useState('login'); // 'login' | 'signup' | 'verify'
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [code, setCode] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [open, setOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState(readCachedSettings());

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSiteSettings(response.data);
        writeCachedSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const resetAll = () => {
    setStep('login');
    setFormData({ name: '', email: '', password: '' });
    setCode('');
    setPendingEmail('');
    setResendCooldown(0);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.password,
      });
      login(res.data.access_token, res.data.user);
      toast.success('Signed in.');
      setOpen(false);
      resetAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sign in failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/register`, formData);
      setPendingEmail(formData.email);
      setStep('verify');
      setResendCooldown(30);
      toast.success('Verification code sent — check your email.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create account.');
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/verify-email`, {
        email: pendingEmail,
        code: code.trim(),
      });
      login(res.data.access_token, res.data.user);
      toast.success('Account verified! Welcome.');
      setOpen(false);
      resetAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/auth/resend-code`, { email: pendingEmail });
      toast.success('New code sent.');
      setResendCooldown(30);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not resend code.');
    } finally {
      setBusy(false);
    }
  };

  const primaryColor = siteSettings?.primary_color || HBE.bronze;

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetAll(); }}>
      <DialogTrigger asChild>
        <Button
          className="text-white"
          style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
          data-testid="header-sign-in-btn"
        >
          <User className="h-4 w-4 mr-2" />
          Sign In
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[440px] p-0 overflow-hidden">
        {/* Decorative header */}
        <div
          className="px-6 pt-6 pb-5 text-white"
          style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, #6b4423 100%)` }}
        >
          <DialogHeader className="text-white space-y-1">
            <DialogTitle className="text-white text-2xl tracking-wide">
              {step === 'login' && 'Welcome Back'}
              {step === 'signup' && 'Create Your Account'}
              {step === 'verify' && 'Verify Your Email'}
            </DialogTitle>
            <DialogDescription className="text-white/80">
              {step === 'login' && 'Sign in to continue browsing our collection.'}
              {step === 'signup' && 'Join Historic Bill Exchange in just a moment.'}
              {step === 'verify' && (
                <>We sent a 6-digit code to <strong>{pendingEmail}</strong>.</>
              )}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-6 hbe-paper/40">
          {step === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
              <div>
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <Label htmlFor="login-password">Password</Label>
                <Input
                  id="login-password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  data-testid="login-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="w-full text-white text-base py-5"
                style={{ backgroundColor: primaryColor }}
                data-testid="login-submit-btn"
              >
                {busy ? 'Signing in…' : 'Sign In'}
              </Button>
              <button
                type="button"
                onClick={() => setStep('signup')}
                className="w-full text-sm hbe-ink-text hover:underline"
                data-testid="switch-to-signup-btn"
              >
                New here? Create an account →
              </button>
            </form>
          )}

          {step === 'signup' && (
            <form onSubmit={handleSignup} className="space-y-4" data-testid="signup-form">
              <div>
                <Label htmlFor="signup-name">Full Name</Label>
                <Input
                  id="signup-name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="signup-name-input"
                />
              </div>
              <div>
                <Label htmlFor="signup-email">Email</Label>
                <Input
                  id="signup-email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="signup-email-input"
                />
              </div>
              <div>
                <Label htmlFor="signup-password">Password</Label>
                <Input
                  id="signup-password"
                  type="password"
                  required
                  minLength={6}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  data-testid="signup-password-input"
                />
                <p className="text-xs hbe-muted-text mt-1">Minimum 6 characters.</p>
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="w-full text-white text-base py-5"
                style={{ backgroundColor: primaryColor }}
                data-testid="signup-submit-btn"
              >
                {busy ? 'Sending code…' : 'Send Verification Code'}
              </Button>
              <button
                type="button"
                onClick={() => setStep('login')}
                className="w-full text-sm hbe-ink-text hover:underline"
                data-testid="switch-to-login-btn"
              >
                ← Already have an account? Sign in
              </button>
            </form>
          )}

          {step === 'verify' && (
            <form onSubmit={handleVerify} className="space-y-4" data-testid="verify-form">
              <div>
                <Label htmlFor="verify-code">6-Digit Code</Label>
                <Input
                  id="verify-code"
                  inputMode="numeric"
                  maxLength={6}
                  autoFocus
                  placeholder="••••••"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="text-center text-2xl tracking-[0.6em] font-mono"
                  data-testid="verify-code-input"
                />
                <p className="text-xs hbe-muted-text mt-1">Code expires in 10 minutes.</p>
              </div>
              <Button
                type="submit"
                disabled={busy || code.length !== 6}
                className="w-full text-white text-base py-5"
                style={{ backgroundColor: primaryColor }}
                data-testid="verify-submit-btn"
              >
                {busy ? 'Verifying…' : 'Verify & Continue'}
              </Button>
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => setStep('signup')}
                  className="hbe-ink-text hover:underline"
                  data-testid="verify-back-btn"
                >
                  ← Wrong email?
                </button>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || busy}
                  className="hbe-ink-text hover:underline disabled:text-gray-400 disabled:no-underline"
                  data-testid="verify-resend-btn"
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FullscreenImageViewer({ images, currentIndex, onIndexChange, onClose, rotation = 0, title = '' }) {
  const [zoom, setZoom] = useState(1);
  const containerRef = useRef(null);
  const total = images.length;

  // Reset zoom whenever the visible image changes or viewer opens.
  useEffect(() => { setZoom(1); }, [currentIndex]);

  // Wheel zoom — bound as a non-passive listener so we can preventDefault
  // and stop the page behind the modal from scrolling.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const handler = (e) => {
      e.preventDefault();
      setZoom((z) => {
        const next = z + (e.deltaY < 0 ? 0.2 : -0.2);
        return Math.min(6, Math.max(1, Number(next.toFixed(2))));
      });
    };
    node.addEventListener('wheel', handler, { passive: false });
    return () => node.removeEventListener('wheel', handler);
  }, []);

  // Close on Escape, navigate with arrow keys.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && total > 1) onIndexChange((currentIndex - 1 + total) % total);
      else if (e.key === 'ArrowRight' && total > 1) onIndexChange((currentIndex + 1) % total);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [currentIndex, total, onClose, onIndexChange]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center" data-testid="fullscreen-viewer">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose}></div>

      <div ref={containerRef} className="relative w-full h-full flex items-center justify-center p-4 select-none">
        <button
          onClick={onClose}
          className="absolute top-6 right-6 z-10 bg-black/40 hover:bg-black/60 text-white rounded-full p-3 transition-colors"
          data-testid="fullscreen-close"
          aria-label="Close"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <img
          src={images[currentIndex]}
          alt={title}
          className="max-w-full max-h-full object-contain transition-transform duration-75 ease-out"
          style={{ transform: `rotate(${rotation}deg) scale(${zoom})`, cursor: zoom > 1 ? 'zoom-out' : 'zoom-in' }}
          decoding="async"
          draggable={false}
          onClick={() => setZoom((z) => (z > 1 ? 1 : 2))}
        />

        {/* Zoom indicator */}
        <div className="absolute top-6 left-6 bg-black/40 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-2">
          <span>{Math.round(zoom * 100)}%</span>
          {zoom > 1 && (
            <button
              type="button"
              onClick={() => setZoom(1)}
              className="underline"
              data-testid="fullscreen-zoom-reset"
            >
              Reset
            </button>
          )}
          <span className="hidden sm:inline opacity-70">• Scroll to zoom</span>
        </div>

        {total > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous image"
              onClick={() => onIndexChange((currentIndex - 1 + total) % total)}
              className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 flex items-center gap-1 sm:gap-2 px-2 py-2 sm:px-5 sm:py-3 rounded-full border-2 border-white/40 bg-white/10 backdrop-blur-sm text-white font-semibold"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="hidden sm:inline text-sm">Prev</span>
            </button>
            <button
              type="button"
              aria-label="Next image"
              onClick={() => onIndexChange((currentIndex + 1) % total)}
              className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 flex items-center gap-1 sm:gap-2 px-2 py-2 sm:px-5 sm:py-3 rounded-full border-2 border-white/40 bg-white/10 backdrop-blur-sm text-white font-semibold"
            >
              <span className="hidden sm:inline text-sm">Next</span>
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full">
              {currentIndex + 1} / {total}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ProductDetailPage({ asModal = false }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatDisplayPrice } = useCurrency();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [siteSettings, setSiteSettings] = useState(readCachedSettings());
  const [addingToCart, setAddingToCart] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // When acting as a modal (opened from a product card), close on Escape.
  useEffect(() => {
    if (!asModal) return;
    const onKey = (e) => { if (e.key === 'Escape') navigate(-1); };
    window.addEventListener('keydown', onKey);
    // Prevent the underlying page from scrolling while the modal is open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [asModal, navigate]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/settings`).then(r => { if (!cancelled) { setSiteSettings(r.data); writeCachedSettings(r.data); } }).catch(() => {});
    axios.get(`${API}/products`)
      .then(r => {
        if (cancelled) return;
        const found = (r.data || []).find(p => slugify(p.title) === slug);
        if (found) {
          setProduct(found);
          setActiveImageIdx(0);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => { if (!cancelled) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const primaryColor = siteSettings?.primary_color || HBE.bronze;
  const textColor = siteSettings?.text_color || HBE.ink;
  const borderColor = siteSettings?.border_color || HBE.stone;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center hbe-paper">
        <p className="hbe-ink-text" data-testid="product-detail-loading">Loading product...</p>
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center hbe-paper px-4 text-center">
        <h1 className="text-3xl font-bold hbe-ink-text mb-3" data-testid="product-detail-notfound">Product not found</h1>
        <p className="hbe-muted-text mb-6">We couldn't find a bill matching this link.</p>
        <Button onClick={() => navigate('/products')} data-testid="product-detail-back-btn">
          Browse all products
        </Button>
      </div>
    );
  }

  const images = [product.image_url, ...(product.image_urls || [])].filter(Boolean);
  const activeImage = images[activeImageIdx] || product.image_url;

  const discountedPrice = product.discount_percentage > 0
    ? Math.max(0, (product.price || 0) - product.discount_percentage)
    : product.price;

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Please sign in to add items to cart');
      return;
    }
    if (addingToCart) return;
    setAddingToCart(true);
    try {
      await axios.post(`${API}/cart/add`, null, { params: { product_id: product.id } });
      toast.success('Item added to cart!');
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail === 'Item already in cart') {
        toast.error('Item is already in your cart');
      } else {
        toast.error('Error adding item to cart');
      }
    } finally {
      setAddingToCart(false);
    }
  };

  return (
    <div
      className={
        asModal
          ? "fixed inset-0 z-40 overflow-y-auto bg-black/40 backdrop-blur-md"
          : "min-h-screen py-8 px-4"
      }
      onClick={asModal ? (e) => { if (e.target === e.currentTarget) navigate(-1); } : undefined}
      data-testid="product-detail-page"
    >
      <div
        className={
          asModal
            ? "min-h-full flex items-start justify-center py-4 md:py-6 px-3 md:px-6"
            : "max-w-7xl mx-auto"
        }
        onClick={asModal ? (e) => { if (e.target === e.currentTarget) navigate(-1); } : undefined}
      >
        <div className={asModal ? "max-w-7xl w-full relative" : ""}>
        {asModal ? (
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Close"
            className="absolute -top-2 -right-2 md:-top-3 md:-right-3 z-10 bg-white shadow-md hover:bg-gray-100 hbe-ink-text rounded-full p-2 transition-colors"
            data-testid="product-detail-close-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        ) : (
          <button
            onClick={() => navigate(-1)}
            className="text-sm mb-4 hover:underline"
            style={{ color: primaryColor }}
            data-testid="product-detail-back-link"
          >
            ← Back
          </button>
        )}

        <div className="grid md:grid-cols-2 gap-8 bg-white rounded-2xl shadow-2xl border-2 p-6 sm:p-8" style={{ borderColor: borderColor }}>
          <div>
            <div className="relative w-full rounded-lg overflow-hidden hbe-paper flex items-center justify-center min-h-[260px] md:min-h-[520px] p-4 md:p-8" data-testid="product-detail-image-frame">
              {/* Justin 2026-10-08: halve the cream borders on mobile product pages (was min-h-[380px]) */}
              <img
                src={activeImage}
                alt={product.title}
                className="max-w-full w-auto h-auto max-h-[min(72vh,640px)] object-contain cursor-zoom-in"
                style={{ transform: `rotate(${product.image_rotation || 0}deg)` }}
                loading="lazy"
                decoding="async"
                data-testid="product-detail-image"
                onClick={() => setIsFullscreen(true)}
                onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1580519542036-c47de6196ba5?w=600&q=70'; }}
              />
              {product.is_sold && !product.is_display_only && (
                <Badge className="absolute top-2 right-2 bg-red-600 text-white font-bold shadow" data-testid="product-detail-sold">
                  SOLD
                </Badge>
              )}
              {product.is_display_only && (
                <Badge className="hbe-display-only-badge absolute top-3 left-2 md:top-4 bg-blue-600 text-white font-bold shadow z-[2]" data-testid="product-detail-collection">
                  Display Only
                </Badge>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex flex-wrap gap-2 mt-3 justify-center">
                {images.map((url, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActiveImageIdx(i)}
                    className="rounded-md border-2 overflow-hidden w-16 h-16"
                    style={{ borderColor: i === activeImageIdx ? primaryColor : borderColor }}
                    data-testid={`product-detail-thumb-${i}`}
                  >
                    <img src={url} alt="" className="w-full h-full object-contain" loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col space-y-4">
            <h1 className="text-2xl md:text-3xl font-bold hbe-ink-text" data-testid="product-detail-title">
              {product.title}
            </h1>
            {product.grade && (
              <span className="hbe-grade-badge self-start" data-testid="product-detail-grade">
                {product.grade}
              </span>
            )}
            {catalogueLine(product) && (
              <div className="hbe-collector-key" data-testid="product-detail-catalogue">
                <span className="lbl">Catalogue</span>
                {catalogueLine(product)}
              </div>
            )}

            <div data-testid="product-detail-price">
              <span className="hbe-price text-3xl font-bold">
                {formatDisplayPrice(product.price || 0)}
              </span>
            </div>

            <div>
              <div
                className="rounded-xl p-5 shadow-sm"
                style={{
                  backgroundColor: `${primaryColor}1F`,
                }}
                data-testid="product-detail-description-box"
              >
                <p
                  className="leading-relaxed whitespace-pre-line font-serif text-base font-semibold"
                  style={{ color: textColor }}
                  data-testid="product-detail-description"
                >
                  {product.description}
                </p>
              </div>
            </div>

            {(product.country || product.year || product.denomination) && (
              <div className="flex flex-wrap gap-2" data-testid="product-detail-info">
                {product.country && (
                  <span
                    className="px-3 py-1 rounded-full text-sm font-medium border-2 bg-white"
                    style={{ borderColor: primaryColor, color: textColor }}
                  >
                    <span className="font-semibold">Country:</span> {product.country}
                  </span>
                )}
                {product.year && (
                  <span
                    className="px-3 py-1 rounded-full text-sm font-medium border-2 bg-white"
                    style={{ borderColor: primaryColor, color: textColor }}
                  >
                    <span className="font-semibold">Year:</span> {product.year}
                  </span>
                )}
                {product.denomination && (
                  <span
                    className="px-3 py-1 rounded-full text-sm font-medium border-2 bg-white"
                    style={{ borderColor: primaryColor, color: textColor }}
                  >
                    <span className="font-semibold">Denomination:</span> {product.denomination}
                  </span>
                )}
              </div>
            )}

            <Button
              type="button"
              onClick={handleAddToCart}
              disabled={!user || addingToCart || product.is_sold || product.is_display_only || product.price === 0}
              className="w-full text-white text-base py-6"
              style={{ backgroundColor: (product.is_sold || product.is_display_only || product.price === 0) ? '#6b7280' : primaryColor }}
              data-testid="product-detail-add-to-cart"
            >
              {product.is_display_only
                ? 'Display Only'
                : product.is_sold
                  ? 'SOLD'
                  : product.price === 0
                    ? 'Contact for Price'
                    : (addingToCart ? 'Adding...' : 'Add to Cart')}
            </Button>
            {product.is_display_only && (
              <p className="text-center italic text-blue-700 text-sm" data-testid="product-detail-display-only-note">
                Display only, Personal Collection
              </p>
            )}
            {!user && !product.is_sold && !product.is_display_only && product.price !== 0 && (
              <p className="text-xs text-center text-gray-500">Sign in to add items to your cart.</p>
            )}
          </div>
        </div>
        </div>
      </div>

      {isFullscreen && (
        <FullscreenImageViewer
          images={images}
          currentIndex={activeImageIdx}
          onIndexChange={setActiveImageIdx}
          onClose={() => setIsFullscreen(false)}
          rotation={product.image_rotation || 0}
          title={product.title}
        />
      )}
    </div>
  );
}

function CounterOfferPage() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [counterPrice, setCounterPrice] = useState(searchParams.get('price') || '');
  const [counterMessage, setCounterMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/offers/${offerId}`)
      .then(r => setOffer(r.data))
      .catch(() => toast.error('Offer not found'))
      .finally(() => setLoading(false));
  }, [offerId]);

  const sendCounter = async () => {
    const amt = parseFloat(counterPrice);
    if (!amt || amt <= 0) return toast.error('Enter a valid counter price');
    setSending(true);
    try {
      await axios.post(`${API}/admin/offers/${offerId}/counter`, {
        counter_price: amt,
        message: counterMessage.trim(),
      });
      toast.success('Counter-offer sent!');
      setSent(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send counter-offer');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center hbe-paper"><p className="hbe-ink-text">Loading offer...</p></div>;
  if (!offer) return <div className="min-h-screen flex items-center justify-center hbe-paper"><p className="text-red-700">Offer not found.</p></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F7F4EF] to-white py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate('/admin')} className="text-sm hbe-muted-text mb-4 hover:underline">← Back to Admin</button>
        <div className="bg-white rounded-2xl shadow-lg border-2 hbe-stone-border p-6 sm:p-8" data-testid="counter-offer-page">
          <h1 className="text-2xl font-bold hbe-ink-text mb-2">Counter-Offer</h1>
          <p className="text-sm hbe-muted-text mb-6">Review the offer and send a counter price to the buyer.</p>

          <div className="hbe-paper border hbe-stone-border rounded-lg p-4 mb-6 space-y-1">
            <div className="text-xs hbe-muted-text">Product</div>
            <div className="font-semibold hbe-ink-text">{offer.product?.title || offer.product_title}</div>
            <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
              <div><span className="hbe-muted-text">Listed:</span> <span className="font-medium">${(offer.product?.price || 0).toFixed(2)}</span></div>
              <div><span className="hbe-muted-text">Buyer offer:</span> <span className="font-bold text-green-700">${offer.offer_amount.toFixed(2)}</span></div>
              <div className="col-span-2"><span className="hbe-muted-text">From:</span> {offer.customer_name} — <a href={`mailto:${offer.customer_email}`} className="hbe-muted-text underline">{offer.customer_email}</a></div>
              {offer.message && <div className="col-span-2 text-xs hbe-muted-text italic border-t hbe-stone-border pt-2 mt-1">"{offer.message}"</div>}
            </div>
          </div>

          {sent ? (
            <div className="text-center py-6 space-y-3">
              <p className="text-lg font-semibold text-green-700">Counter-offer sent!</p>
              <p className="text-sm hbe-muted-text">The buyer has been emailed at {offer.customer_email}.</p>
              <button onClick={() => navigate('/admin')} className="mt-4 hbe-ink-fill hover:opacity-90 text-white px-6 py-2 rounded-lg">Back to Admin</button>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium hbe-ink-text mb-1 block">Your Counter Price (CAD $)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={counterPrice}
                  onChange={(e) => setCounterPrice(e.target.value)}
                  className="w-full text-2xl font-bold border-2 hbe-stone-border rounded-lg px-4 py-3 focus:border-[#8B7355] focus:outline-none"
                  data-testid="counter-price-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium hbe-ink-text mb-1 block">Optional Message to Buyer</label>
                <textarea
                  rows={3}
                  value={counterMessage}
                  onChange={(e) => setCounterMessage(e.target.value)}
                  placeholder="e.g. Best I can do — shipping included."
                  className="w-full border-2 hbe-stone-border rounded-lg px-3 py-2 focus:border-[#8B7355] focus:outline-none"
                  data-testid="counter-message-input"
                />
              </div>
              <button
                onClick={sendCounter}
                disabled={sending}
                className="w-full hbe-ink-fill hover:opacity-90 text-white font-semibold py-3 rounded-lg disabled:opacity-50"
                data-testid="send-counter-btn"
              >
                {sending ? 'Sending...' : 'Send Counter-Offer'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function HomePage() {
  const navigate = useNavigate();
  const [siteSettings, setSiteSettings] = useState(readCachedSettings());
  const [categoryImages, setCategoryImages] = useState([]);
  const [dynamicCategories, setDynamicCategories] = useState([]);

  useEffect(() => {
    fetchSiteSettings();
    fetchCategoryImages();
    axios.get(`${API}/categories`).then(r => setDynamicCategories(r.data || [])).catch(() => {});
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSiteSettings(response.data);
      writeCachedSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchCategoryImages = async () => {
    try {
      const response = await axios.get(`${API}/category-images`);
      setCategoryImages(response.data);
    } catch (error) {
      console.error('Error fetching category images:', error);
    }
  };

  const getCategoryImage = (category) => {
    const categoryImg = categoryImages.find(img => img.category === category);
    return categoryImg?.image_url || 'https://images.unsplash.com/photo-1580519542036-c47de6196ba5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHx8MTc1ODM0MDgzMXww&ixlib=rb-4.1.0&q=85';
  };

  const categoryIconMap = {
    'Canadian Graded Bills': Award,
    'Canadian Coins': Coins,
    'Silver Bullion': DollarSign,
    'Silver': DollarSign,
    'Graded World Bills': Globe,
    'Silver Jewelry': Star,
  };

  const categories = dynamicCategories.map((c) => ({
    id: c.name,
    title: c.name,
    description: c.description || '',
    icon: categoryIconMap[c.name] || Award,
  }));

  const getBackgroundStyle = () => {
    if (!siteSettings) return {};
    
    let style = {};
    
    if (siteSettings.background_type === 'solid') {
      style.backgroundColor = siteSettings.background_value;
    } else if (siteSettings.background_type === 'gradient') {
      style.background = siteSettings.background_value;
    } else if (siteSettings.background_type === 'image') {
      style.backgroundImage = `url(${siteSettings.background_value})`;
      style.backgroundSize = 'cover';
      style.backgroundPosition = 'center';
      style.backgroundAttachment = 'fixed';
      style.backgroundRepeat = 'no-repeat';
    }
    
    return style;
  };

  const primaryColor = siteSettings?.primary_color || HBE.bronze;
  const secondaryColor = siteSettings?.secondary_color || HBE.ink;
  const accentColor = siteSettings?.accent_color || HBE.bronze;
  const textColor = siteSettings?.text_color || HBE.ink;
  const borderColor = siteSettings?.border_color || HBE.stone;

  const overlayStyle = siteSettings?.background_overlay && siteSettings?.background_type === 'image' ? {
    background: `linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 50%, rgba(255,255,255,0.9) 100%)`
  } : {};

  return (
    <div className="min-h-screen relative" style={getBackgroundStyle()}>
      {/* Overlay for background images */}
      {siteSettings?.background_overlay && siteSettings?.background_type === 'image' && (
        <div className="absolute inset-0 z-0" style={overlayStyle}></div>
      )}
      
      <div className="relative z-10">
        {/* Optional side banners (hidden on smaller screens) */}
        {siteSettings?.side_banner_left_url && (
          <div className="pointer-events-none hidden lg:block fixed left-0 top-0 h-full w-40 xl:w-52 z-0" aria-hidden="true">
            <img src={siteSettings.side_banner_left_url} alt="" className="w-full h-full object-contain" />
            {siteSettings?.side_banner_fade !== false && (
              <div className="absolute inset-0" style={{ background: `linear-gradient(to right, transparent 0%, transparent 40%, ${siteSettings.background_type === 'solid' ? siteSettings.background_value : '#ffffff'} 100%)` }} />
            )}
          </div>
        )}
        {siteSettings?.side_banner_right_url && (
          <div className="pointer-events-none hidden lg:block fixed right-0 top-0 h-full w-40 xl:w-52 z-0" aria-hidden="true">
            <img src={siteSettings.side_banner_right_url} alt="" className="w-full h-full object-contain" />
            {siteSettings?.side_banner_fade !== false && (
              <div className="absolute inset-0" style={{ background: `linear-gradient(to left, transparent 0%, transparent 40%, ${siteSettings.background_type === 'solid' ? siteSettings.background_value : '#ffffff'} 100%)` }} />
            )}
          </div>
        )}

        {/* Hero Section — title + tagline only */}
        <section className="relative pt-6 sm:pt-20 pb-4 px-4">
          <div className="container mx-auto text-center">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-3 sm:mb-6 font-serif leading-tight" style={{ color: textColor }}>
                {siteSettings?.site_title || 'Historical Bill Exchange'}
              </h1>
              <p className="text-base sm:text-xl leading-relaxed opacity-90 whitespace-pre-line" style={{ color: textColor }}>
                {siteSettings?.hero_tagline || 'Discover rare, collectible banknotes and coins from Canada to worldwide.\nEach piece is carefully authenticated and graded for serious collectors, by serious grading companies.'}
              </p>
            </div>
          </div>
        </section>

        {/* Categories Section — Justin 2026-10-19: tighter bottom padding on mobile (gap above Authenticated Quality) */}
        <section className="pt-2 pb-6 sm:pb-16 px-4">
          <div className="mx-auto" style={{ maxWidth: HBE.contentMax }}>
            <h2 className="text-4xl font-bold text-center mb-12 font-serif" style={{ color: textColor }}>
              Our Collections
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {categories.map((category) => {
                const IconComponent = category.icon;
                return (
                  <Card 
                    key={category.id} 
                    className="group hover:shadow-xl transition-all duration-300 cursor-pointer bg-white/90 backdrop-blur-sm"
                    style={{ borderColor: borderColor }}
                    onClick={() => navigate(`/products?category=${encodeURIComponent(category.id)}`)}
                  >
                    <div className="relative h-48 overflow-hidden rounded-t-lg">
                      <img 
                        src={thumbUrl(getCategoryImage(category.id))} 
                        alt={category.title}
                        className="w-full h-full object-contain  transition-transform duration-300"
                        loading="lazy"
                        onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1580519542036-c47de6196ba5?w=600&q=70'; }}
                      />
                      <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-2 rounded-full">
                        <IconComponent className="h-5 w-5" style={{ color: primaryColor }} />
                      </div>
                    </div>
                    <CardHeader>
                      <CardTitle className="group-hover:opacity-80 transition-colors" style={{ color: textColor }}>
                        {category.title}
                      </CardTitle>
                      <CardDescription style={{ color: textColor, opacity: 0.8 }}>
                        {category.description}
                      </CardDescription>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Features Section — Justin 2026-10-19: tighter vertical padding on mobile */}
        <section className="py-6 sm:py-16 px-4">
          <div 
            className="rounded-2xl mx-4 p-6 sm:p-8"
            style={{ 
              background: `linear-gradient(135deg, ${primaryColor}20, ${secondaryColor}20)`,
              borderColor: borderColor
            }}
          >
            <div className="container mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                {/* Justin 2026-10-19: personal-collection statement and Authenticated Quality swapped places */}
                <div className="space-y-4 flex items-center justify-center">
                  <p className="text-base leading-relaxed font-serif italic" style={{ color: textColor, opacity: 0.9 }}>
                    This is my personal collection website to display and share my inventory of 1800s and 1900s Canadian Currency, Silver and Numismatics.
                  </p>
                </div>
                <div className="space-y-4">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Star className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold" style={{ color: textColor }}>Authenticated Quality</h3>
                  <p style={{ color: textColor, opacity: 0.8 }}>Every item is carefully verified and graded by professionals</p>
                </div>
                <div className="space-y-4">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Award className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold" style={{ color: textColor }}>Collector Focused</h3>
                  <p style={{ color: textColor, opacity: 0.8 }}>Curated specifically for serious numismatic collectors</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function ProductsPage() {
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get('category') || '';
  const [products, setProducts] = useState(() => readCachedProducts(initialCategory) || []);
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const searchTerm = (searchParams.get('search') || '').trim().toLowerCase();
  const [siteSettings, setSiteSettings] = useState(readCachedSettings());
  const { formatDisplayPrice } = useCurrency();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [cardImageIndex, setCardImageIndex] = useState({}); // { [productId]: index }
  const [isFullscreenImage, setIsFullscreenImage] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [offerForm, setOfferForm] = useState({ name: '', email: '', amount: '', message: '' });
  const [submittingOffer, setSubmittingOffer] = useState(false);
  const [mobileCols, setMobileCols] = useState(2); // 1, 2, or 3 wide on mobile
  const [desktopCols, setDesktopCols] = useState(3); // 1, 2, or 3 wide on desktop, default 3
  const [sortFilter, setSortFilter] = useState('oldest');
  // Country filter for graded bills / Graded World Bills (All Countries = no filter)
  const [countryFilter, setCountryFilter] = useState('__all__');
  // Coin subsection toggles for the Silver Coins category (Justin's 2026-10-03 request,
  // rewired to the renamed category + UK subsection 2026-10-04)
  const [coinSubsection, setCoinSubsection] = useState(''); // '' | 'canadian-silver' | 'newfoundland-silver' | 'uk-silver'
  const [coinDenom, setCoinDenom] = useState(''); // '' = no subsection selected; otherwise a specific denomination value (Justin 2026-10-04: no "All denominations" — defaults to highest)
  // When a coin subsection is active, products are pulled across all categories
  // (null = use the current category's products as usual).
  const [coinScopeProducts, setCoinScopeProducts] = useState(null);
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onResize = () => setIsMobileViewport(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Keep selectedCategory in sync with the ?category= URL param (so tapping a homepage tile filters)
  useEffect(() => {
    const cat = searchParams.get('category') || '';
    setSelectedCategory(cat);
  }, [searchParams]);

  // When entering a graded-bills category (or leaving one), reset country to All Countries.
  // Also reset the coin subsection toggles whenever the category changes.
  useEffect(() => {
    setCountryFilter('__all__');
    setCoinSubsection('');
    setCoinDenom('');
    setCoinScopeProducts(null);
  }, [selectedCategory]);

  const isGradedBillsCategory = (cat) => {
    const c = (cat || '').toLowerCase();
    if (!c) return false;
    if (c === 'graded world bills' || c === 'canadian graded bills') return true;
    // Soft match for future graded-bill category names
    return c.includes('graded') && (c.includes('bill') || c.includes('world'));
  };
  const showCountryFilter = isGradedBillsCategory(selectedCategory);

  const countryOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const p of products) {
      const c = (p?.country || '').trim();
      if (!c || c.toLowerCase() === 'other') continue;
      if (!seen.has(c.toLowerCase())) {
        seen.add(c.toLowerCase());
        out.push(c);
      }
    }
    return out.sort((a, b) => a.localeCompare(b));
  }, [products]);

  const displayedProducts = useMemo(() => {
    // When a coin subsection is active, filter across all categories;
    // otherwise use the current category's products as usual.
    let list = coinScopeProducts || [...products];
    // Search filter across title, description, grade, denomination, year, category, country
    if (searchTerm) {
      list = list.filter((p) => {
        const haystack = [
          p.title,
          p.description,
          p.grade,
          p.denomination,
          p.category,
          p.country,
          p.year != null ? String(p.year) : '',
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(searchTerm);
      });
    }
    // Optional country filter (graded bills / Graded World Bills) — does not reset View/sort
    if (countryFilter && countryFilter !== '__all__') {
      const want = countryFilter.toLowerCase();
      list = list.filter((p) => ((p.country || '').trim().toLowerCase() === want));
    }
    // Coin subsection filter (Silver Coins category) — client-side keyword
    // match until backend categories/fields exist for these subsections.
    // The product pool spans all categories while a subsection is active,
    // because the Newfoundland (and future UK) pieces live in their own
    // categories.
    if (selectedCategory === SILVER_COINS_CATEGORY && coinSubsection) {
      const subMatch = COIN_SUBSECTION_MATCH[coinSubsection];
      if (subMatch) list = list.filter((p) => subMatch(coinHaystack(p)));
      if (coinDenom) {
        const match = COIN_DENOM_MATCH[coinDenom];
        if (match) list = list.filter((p) => match(coinHaystack(p)));
      }
    }
    if (sortFilter === 'available') list = list.filter(p => !p.is_sold && p.is_available !== false);
    else if (sortFilter === 'sold') list = list.filter(p => p.is_sold);
    // 'all' keeps everything
    switch (sortFilter) {
      case 'oldest': {
        // Year ascending; missing/invalid year last. Same year: lower grade first (worse→better).
        list.sort((a, b) => {
          const ya = yearNum(a);
          const yb = yearNum(b);
          if (ya === null && yb === null) return gradeRank(a) - gradeRank(b);
          if (ya === null) return 1;
          if (yb === null) return -1;
          if (ya !== yb) return ya - yb;
          return gradeRank(a) - gradeRank(b);
        });
        break;
      }
      case 'newest': {
        // Year descending; missing/invalid year last. Same year: higher grade first.
        list.sort((a, b) => {
          const ya = yearNum(a);
          const yb = yearNum(b);
          if (ya === null && yb === null) return gradeRank(b) - gradeRank(a);
          if (ya === null) return 1;
          if (yb === null) return -1;
          if (ya !== yb) return yb - ya;
          return gradeRank(b) - gradeRank(a);
        });
        break;
      }
      case 'price-low':
        list.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case 'price-high':
        list.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
      case 'featured': {
        // Only show products explicitly flagged featured in the admin panel.
        // No fallback — an empty grid signals "you haven't featured anything yet."
        list = list.filter(p => p.is_featured === true);
        list.sort((a, b) => {
          const ao = a.featured_order;
          const bo = b.featured_order;
          const aHas = ao !== null && ao !== undefined && ao !== '';
          const bHas = bo !== null && bo !== undefined && bo !== '';
          if (aHas && bHas) return Number(ao) - Number(bo);
          if (aHas) return -1;
          if (bHas) return 1;
          return new Date(b.created_at) - new Date(a.created_at);
        });
        break;
      }
      case 'available':
      case 'sold':
      default:
        // Keep the backend's default ordering.
        break;
    }
    // Justin (2026-10-05): Coin subsection sorting overrides the sort dropdown.
    // - "All denominations" (coinDenom empty): show coins largest denomination to lowest.
    // - Specific denomination: oldest to newest.
    if (selectedCategory === SILVER_COINS_CATEGORY && coinSubsection) {
      const sub = COIN_SUBSECTIONS.find(s => s.id === coinSubsection);
      if (sub) {
        if (!coinDenom) {
          // Sort by denomination order (largest to lowest, as listed in denoms array)
          const denomOrder = sub.denoms.map(d => d.value);
          list.sort((a, b) => {
            const ha = coinHaystack(a);
            const hb = coinHaystack(b);
            const ia = denomOrder.findIndex(v => COIN_DENOM_MATCH[v] && COIN_DENOM_MATCH[v](ha));
            const ib = denomOrder.findIndex(v => COIN_DENOM_MATCH[v] && COIN_DENOM_MATCH[v](hb));
            const ra = ia === -1 ? 999 : ia;
            const rb = ib === -1 ? 999 : ib;
            if (ra !== rb) return ra - rb;
            // Within same denomination: oldest to newest
            const ya = yearNum(a);
            const yb = yearNum(b);
            if (ya === null && yb === null) return 0;
            if (ya === null) return 1;
            if (yb === null) return -1;
            return ya - yb;
          });
        } else {
          // Specific denomination: oldest to newest by year
          list.sort((a, b) => {
            const ya = yearNum(a);
            const yb = yearNum(b);
            if (ya === null && yb === null) return 0;
            if (ya === null) return 1;
            if (yb === null) return -1;
            return ya - yb;
          });
        }
      }
    }
    return list;
  }, [products, coinScopeProducts, sortFilter, searchTerm, countryFilter, selectedCategory, coinSubsection, coinDenom]);

  const [categories, setCategories] = useState([]);
  useEffect(() => {
    axios.get(`${API}/categories`).then(r => setCategories((r.data || []).map(c => c.name))).catch(() => {});
  }, []);

  useEffect(() => {
    // Hydrate immediately from cache so the grid isn't blank between
    // category transitions, then kick off the network refresh in the
    // background.
    const cached = readCachedProducts(selectedCategory);
    if (cached) setProducts(cached);
    fetchProducts(selectedCategory);
    fetchSiteSettings();
  }, [selectedCategory]);

  const fetchProducts = async (category = '') => {
    try {
      const url = category ? `${API}/products?category=${encodeURIComponent(category)}` : `${API}/products`;
      const response = await axios.get(url);
      setProducts(response.data);
      writeCachedProducts(category, response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Error loading products');
    }
  };

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSiteSettings(response.data);
      writeCachedSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const addToCart = async (productId) => {
    if (!user) {
      toast.error('Please sign in to add items to cart');
      return;
    }

    if (addingToCart) {
      return; // Prevent double-clicks
    }

    setAddingToCart(true);
    try {
      await axios.post(`${API}/cart/add`, null, {
        params: { product_id: productId }
      });
      toast.success('Item added to cart!');
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail === 'Item already in cart') {
        toast.error('Item is already in your cart');
      } else {
        toast.error('Error adding item to cart');
      }
    } finally {
      setAddingToCart(false);
    }
  };

  const getDiscountedPrice = (price, discount) => {
    return discount > 0 ? Math.max(0, price - discount) : price;
  };

  // Toggle a coin subsection on/off; switching subsections resets the denomination pick.
  // Picking a subsection pulls products across all categories (so the
  // Newfoundland/UK pieces in their own categories show up).
  // Justin (2026-10-05): "All denominations" is the default first option, showing
  // coins largest denomination to lowest. When a specific denomination is picked,
  // sort oldest to newest.
  const selectCoinSubsection = (id) => {
    if (coinSubsection === id) {
      setCoinSubsection('');
      setCoinDenom('');
      setCoinScopeProducts(null);
    } else {
      setCoinSubsection(id);
      setCoinDenom(''); // '' = All denominations (default, per Justin 2026-10-05)
      axios.get(`${API}/products`).then((r) => setCoinScopeProducts(r.data || [])).catch(() => {});
    }
  };
  const activeCoinSub = COIN_SUBSECTIONS.find((s) => s.id === coinSubsection) || null;

  const primaryColor = siteSettings?.primary_color || HBE.bronze;
  const borderColor = siteSettings?.border_color || HBE.stone;
  const textColor = siteSettings?.text_color || HBE.ink;
  const backgroundStyle = siteSettings ? {
    background: siteSettings.background_type === 'solid' 
      ? siteSettings.background_value
      : siteSettings.background_type === 'gradient'
      ? siteSettings.background_value
      : `url(${siteSettings.background_value}) center/cover`,
  } : {};

  return (
    <div className="min-h-screen py-8" style={backgroundStyle}>
      <div className="mx-auto px-4 md:px-6" style={{ maxWidth: HBE.contentMax }}>
        <div className="mb-8">
          <h1 className="text-4xl font-bold hbe-ink-text mb-4 font-serif">Our Collection</h1>
          <div className="flex flex-wrap gap-2">
            {categories.map(category => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(category)}
                className={selectedCategory === category ? 'text-white' : 'hbe-stone-border hover:bg-[#FBF9F5]'}
                style={selectedCategory === category ? { backgroundColor: primaryColor } : {}}
              >
                {category}
              </Button>
            ))}
          </div>

          {/* Sort & Filter dropdown */}
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-2">
            <label
              htmlFor="sort-filter"
              className="text-sm font-medium"
              style={{ color: textColor }}
            >
              Sort & Filter:
            </label>
            <div className="relative w-full sm:w-64">
              <select
                id="sort-filter"
                value={sortFilter}
                onChange={(e) => setSortFilter(e.target.value)}
                data-testid="sort-filter-select"
                className="w-full appearance-none rounded-lg border-2 bg-white/90 backdrop-blur-sm px-3 py-2 pr-9 text-sm font-medium shadow-sm focus:outline-none focus:ring-2"
                style={{
                  borderColor: primaryColor,
                  color: textColor,
                }}
              >
                <option value="featured">Featured Products</option>
                <option value="oldest">Oldest First</option>
                <option value="newest">Newest First</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="available">Available Only</option>
                <option value="sold">Sold Only</option>
              </select>
              {/* Chevron */}
              <svg
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4"
                fill="none"
                stroke={primaryColor}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            <span className="text-xs sm:ml-auto" style={{ color: textColor, opacity: 0.7 }}>
              {displayedProducts.length} {displayedProducts.length === 1 ? 'result' : 'results'}
              {searchTerm && <> for &ldquo;<span className="font-semibold">{searchTerm}</span>&rdquo;</>}
            </span>
          </div>

          {/* Coin subsection toggles — Silver Coins category only (Justin's 2026-10-03 request,
              rewired to the renamed category + UK subsection 2026-10-04).
              Buttons styled exactly like the category selector above; each subsection reveals
              its denomination dropdown (styled like the Sort & Filter select). */}
          {selectedCategory === SILVER_COINS_CATEGORY && (
            <div className="mt-4 flex flex-col gap-3" data-testid="coin-subsection-wrap">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium" style={{ color: textColor }}>
                  Subsections:
                </span>
                {COIN_SUBSECTIONS.map((sub) => (
                  <Button
                    key={sub.id}
                    type="button"
                    variant={coinSubsection === sub.id ? 'default' : 'outline'}
                    onClick={() => selectCoinSubsection(sub.id)}
                    aria-pressed={coinSubsection === sub.id}
                    data-testid={`coin-subsection-${sub.id}`}
                    className={coinSubsection === sub.id ? 'text-white' : 'hbe-stone-border hover:bg-[#FBF9F5]'}
                    style={coinSubsection === sub.id ? { backgroundColor: primaryColor } : {}}
                  >
                    {sub.label}
                  </Button>
                ))}
              </div>
              {activeCoinSub && activeCoinSub.denoms.length > 0 && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <label
                    htmlFor="coin-denomination"
                    className="text-sm font-medium"
                    style={{ color: textColor }}
                  >
                    Denomination:
                  </label>
                  <div className="relative w-full sm:w-64">
                    <select
                      id="coin-denomination"
                      value={coinDenom}
                      onChange={(e) => setCoinDenom(e.target.value)}
                      data-testid="coin-denomination-select"
                      className="w-full appearance-none rounded-lg border-2 bg-white/90 backdrop-blur-sm px-3 py-2 pr-9 text-sm font-medium shadow-sm focus:outline-none focus:ring-2"
                      style={{
                        borderColor: primaryColor,
                        color: textColor,
                      }}
                    >
                      {/* Justin (2026-10-05): "All denominations" is the default first option */}
                      <option value="">All denominations</option>
                      {activeCoinSub.denoms.map((d) => (
                        <option key={d.value} value={d.value}>{d.label}</option>
                      ))}
                    </select>
                    {/* Chevron */}
                    <svg
                      className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4"
                      fill="none"
                      stroke={primaryColor}
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          )}
          {searchTerm && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => navigate('/products')}
                className="text-xs underline"
                style={{ color: primaryColor }}
                data-testid="clear-search-btn"
              >
                Clear search
              </button>
            </div>
          )}

          {/* Viewing options — 1 / 2 / 3 wide (mobile uses mobileCols, desktop uses desktopCols) */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium" style={{ color: textColor, opacity: 0.8 }}>View:</span>
            {/* Mobile picker */}
            <div className="flex gap-2 md:hidden" role="group" aria-label="Viewing options (mobile)">
              {[1, 2, 3].map((n) => {
                const isActive = mobileCols === n;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setMobileCols(n)}
                    aria-label={`${n} column${n > 1 ? 's' : ''}`}
                    aria-pressed={isActive}
                    data-testid={`view-cols-${n}`}
                    className="h-9 w-9 rounded-md border-2 flex items-center justify-center text-sm font-semibold"
                    style={{
                      backgroundColor: isActive ? primaryColor : '#ffffff',
                      borderColor: primaryColor,
                      color: isActive ? '#ffffff' : primaryColor,
                    }}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
            {/* Desktop picker */}
            <div className="hidden md:flex gap-2" role="group" aria-label="Viewing options (desktop)">
              {[1, 2, 3].map((n) => {
                const isActive = desktopCols === n;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setDesktopCols(n)}
                    aria-label={`${n} column${n > 1 ? 's' : ''}`}
                    aria-pressed={isActive}
                    data-testid={`view-cols-desktop-${n}`}
                    className="h-9 w-9 rounded-md border-2 flex items-center justify-center text-sm font-semibold"
                    style={{
                      backgroundColor: isActive ? primaryColor : '#ffffff',
                      borderColor: primaryColor,
                      color: isActive ? '#ffffff' : primaryColor,
                    }}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
            {/* Country selector — only for graded bills / Graded World Bills; right of View */}
            {showCountryFilter && (
              <div className="flex items-center gap-2 ml-auto sm:ml-4" data-testid="country-filter-wrap">
                <label
                  htmlFor="country-filter"
                  className="text-sm font-medium whitespace-nowrap"
                  style={{ color: textColor, opacity: 0.8 }}
                >
                  Country:
                </label>
                <div className="relative min-w-[10rem]">
                  <select
                    id="country-filter"
                    value={countryFilter}
                    onChange={(e) => setCountryFilter(e.target.value)}
                    data-testid="country-filter-select"
                    className="w-full appearance-none rounded-md border-2 bg-white/90 backdrop-blur-sm px-3 py-1.5 pr-8 text-sm font-medium shadow-sm focus:outline-none focus:ring-2"
                    style={{
                      borderColor: primaryColor,
                      color: textColor,
                    }}
                  >
                    <option value="__all__">All Countries</option>
                    {countryOptions.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <svg
                    className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4"
                    fill="none"
                    stroke={primaryColor}
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            )}
          </div>
        </div>

        <div
          className={`grid gap-3 sm:gap-8 mx-auto ${
            mobileCols === 1 ? 'grid-cols-1 px-4 sm:px-8' : mobileCols === 2 ? 'grid-cols-2' : 'grid-cols-3'
          } ${
            desktopCols === 1 ? 'md:grid-cols-1 md:max-w-[920px] md:px-0' : desktopCols === 2 ? 'md:grid-cols-2 md:max-w-none' : 'md:grid-cols-3 md:max-w-none'
          }`}
        >
          {displayedProducts.map(product => {
            const allImages = [product.image_url, ...(product.image_urls || [])].filter(url => url);
            const activeIdx = cardImageIndex[product.id] ?? 0;
            const activeImage = allImages[activeIdx] || product.image_url;
            const compact = isMobileViewport && mobileCols !== 1; // 2- or 3-column mobile view only
            return (
              <Card key={product.id} className="group hover:shadow-xl transition-shadow duration-300 bg-white cursor-pointer overflow-hidden" style={{ borderColor: borderColor }}
                    data-testid={`product-card-${product.id}`}
                    onClick={() => {
                      navigate(`/product/${slugify(product.title)}`, { state: { backgroundLocation: location } });
                    }}>
                {/* Status strip ABOVE the photo — grade + Display/Sold never overlay the image */}
                <div className={`px-2 pt-2 pb-1.5 flex flex-col gap-1 bg-white border-b ${compact ? 'min-h-[52px]' : 'min-h-[56px]'}`} style={{ borderColor: borderColor }} data-testid={`product-status-strip-${product.id}`}>
                  {product.grade ? (
                    <span
                      className={`hbe-grade-badge self-end ${compact ? 'text-[10px] max-w-[95%] truncate' : ''}`}
                      data-testid={`product-grade-badge-${product.id}`}
                    >
                      {product.grade}
                    </span>
                  ) : (
                    <span className="h-4" aria-hidden="true" />
                  )}
                  <div className="flex items-center justify-between gap-1 w-full min-h-[22px]">
                    <div>
                      {product.is_display_only && (
                        <Badge
                          className={`hbe-display-only-badge bg-blue-600 text-white font-bold shadow ${compact ? 'text-[10px] px-1.5 py-0 md:text-xs md:px-2 md:py-0.5' : ''}`}
                          data-testid={`product-collection-badge-${product.id}`}
                        >
                          Display Only
                        </Badge>
                      )}
                    </div>
                    <div>
                      {product.is_sold && !product.is_display_only && (
                        <Badge
                          className={`bg-red-600 text-white font-bold shadow ${compact ? 'text-[10px] px-1.5 py-0 md:text-xs md:px-2 md:py-0.5' : ''}`}
                          data-testid={`product-sold-badge-${product.id}`}
                        >
                          SOLD
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className={`relative w-full overflow-hidden hbe-paper hbe-card-media flex items-center justify-center px-2 py-2 md:px-3 md:py-3 ${
                  mobileCols === 3
                    ? 'aspect-[5/4] md:aspect-[5/3]'
                    : mobileCols === 2
                    ? 'aspect-[5/4] md:aspect-[5/3]'
                    : 'aspect-[4/3] md:aspect-[5/3]'
                }`}>
                  <ProductImage
                    src={activeImage}
                    alt={product.title}
                    className="relative z-[1] max-h-full max-w-full w-auto h-auto object-contain"
                    testId={`product-image-${product.id}`}
                    thumb
                  />
                  <div className="absolute inset-0 pointer-events-none" style={{ transform: `rotate(${product.image_rotation || 0}deg)` }} aria-hidden="true" />
                </div>
                
                {/* Front/Back switcher — scales with column count */}
                {allImages.length > 1 && (
                  <div
                    className={`${compact ? 'px-2 pt-2 md:px-4 md:pt-3' : 'px-4 pt-3'}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className={`flex flex-wrap justify-center ${compact ? 'gap-1 md:gap-2' : 'gap-2'}`}>
                      {allImages.map((_, index) => {
                        const isActive = index === activeIdx;
                        const label = allImages.length === 2
                          ? (index === 0 ? 'Front' : 'Back')
                          : `Photo ${index + 1}`;
                        return (
                          <button
                            key={index}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCardImageIndex(prev => ({ ...prev, [product.id]: index }));
                            }}
                            aria-label={label}
                            aria-pressed={isActive}
                            data-testid={`product-thumb-${product.id}-${index}`}
                            className={`rounded-full border-2 select-none font-semibold ${
                              compact
                                ? 'px-2 py-0.5 text-[10px] md:px-4 md:py-2 md:text-sm'
                                : 'px-4 py-2 text-sm'
                            }`}
                            style={{
                              backgroundColor: isActive ? primaryColor : '#ffffff',
                              borderColor: primaryColor,
                              color: isActive ? '#ffffff' : primaryColor,
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                <CardHeader className={compact ? 'p-2 md:p-6' : ''}>
                <CardTitle className={`hbe-ink-text leading-tight ${
                  compact ? 'text-xs md:text-lg line-clamp-2' : 'text-lg'
                }`}>
                  {product.title}
                </CardTitle>
                {!compact && (
                  <CardDescription className="hbe-bronze text-sm">
                    {product.description}
                  </CardDescription>
                )}
              </CardHeader>
              {!compact && (
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm hbe-muted-text">Category:</span>
                    <Badge variant="outline" className="hbe-stone-border hbe-muted-text">
                      {product.category}
                    </Badge>
                  </div>
                  {product.year && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm hbe-muted-text">Year:</span>
                      <span className="text-sm font-medium hbe-ink-text">{product.year}</span>
                    </div>
                  )}
                  {product.denomination && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm hbe-muted-text">Denomination:</span>
                      <span className="text-sm font-medium hbe-ink-text">{product.denomination}</span>
                    </div>
                  )}
                </div>
              </CardContent>
              )}
              <CardFooter className={compact ? 'p-2 md:p-6 flex-col items-stretch gap-2' : 'flex items-center justify-between'}>
                <div className={compact ? 'flex flex-col items-center text-center' : 'flex flex-col'}>
                  {product.price === 0 ? (
                    <span className={`font-semibold hbe-ink-text ${compact ? 'text-[11px] md:text-base' : 'text-base'}`} data-testid={`product-contact-price-${product.id}`}>
                      Contact For Price
                    </span>
                  ) : product.discount_percentage > 0 ? (
                    <>
                      <span className={`font-bold text-green-600 ${compact ? 'text-xs md:text-lg' : 'text-lg'}`}>
                        {formatDisplayPrice(getDiscountedPrice(product.price, product.discount_percentage))}
                      </span>
                      <span className={`text-gray-500 line-through ${compact ? 'text-[10px] md:text-sm' : 'text-sm'}`}>
                        {formatDisplayPrice(product.price)}
                      </span>
                    </>
                  ) : (
                    <span className={`font-bold hbe-ink-text ${compact ? 'text-xs md:text-lg' : 'text-lg'}`}>
                      {formatDisplayPrice(product.price)}
                    </span>
                  )}
                </div>
                <Button 
                  onClick={() => addToCart(product.id)}
                  className={`text-white ${compact ? 'w-full text-xs h-8 px-2 md:w-auto md:text-sm md:h-10 md:px-4' : ''}`}
                  style={{ backgroundColor: (product.is_sold || product.is_display_only || product.price === 0) ? '#6b7280' : primaryColor }}
                  disabled={!user || addingToCart || product.is_sold || product.is_display_only || product.price === 0}
                  data-testid={`product-add-to-cart-${product.id}`}
                >
                  {!compact && <ShoppingCart className="h-4 w-4 mr-2" />}
                  {product.is_display_only
                    ? (compact ? 'Display' : 'Display Only')
                    : product.is_sold
                      ? 'SOLD'
                      : product.price === 0
                        ? (compact ? 'Ask Price' : 'Contact For Price')
                        : (addingToCart ? 'Adding...' : (compact ? 'Add' : 'Add to Cart'))}
                </Button>
                {product.is_display_only && (
                  <p className={`w-full text-center italic text-blue-700 ${compact ? 'text-[10px] mt-1 md:text-xs' : 'text-xs mt-1'}`}
                    data-testid={`product-display-only-note-${product.id}`}>
                    Display only, Personal Collection
                  </p>
                )}
              </CardFooter>
            </Card>
            );
          })}
        </div>

        {displayedProducts.length === 0 && (
          <div className="text-center py-16">
            <p className="text-xl hbe-muted-text">No products found in this category.</p>
            <Button 
              onClick={() => setSelectedCategory('')}
              className="mt-4 text-white"
              style={{ backgroundColor: primaryColor }}
            >
              View All Products
            </Button>
          </div>
        )}
      </div>

      {/* Product Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Blurred Background */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setSelectedProduct(null)}
          ></div>
          
          {/* Modal Content */}
          <div className="relative bg-white rounded-lg shadow-2xl max-w-7xl w-full mx-3 md:mx-6 max-h-[96vh] overflow-y-auto">
            {/* Close Button */}
            <button 
              onClick={() => setSelectedProduct(null)}
              className="absolute top-4 right-4 z-10 bg-black/20 hover:bg-black/40 text-white rounded-full p-2 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="grid md:grid-cols-2 gap-6 p-6">
              {/* Image Carousel */}
              <div className="relative">
                <div className="rounded-lg overflow-hidden hbe-paper relative group flex items-center justify-center min-h-[360px] md:min-h-[480px] p-4 md:p-6">
                  <img 
                    src={[selectedProduct.image_url, ...(selectedProduct.image_urls || [])].filter(url => url)[currentImageIndex]}
                    alt={selectedProduct.title}
                    className="max-w-full max-h-[min(70vh,560px)] w-auto h-auto object-contain cursor-pointer"
                    style={{ transform: `rotate(${selectedProduct.image_rotation || 0}deg)` }}
                    loading="lazy"
                    decoding="async"
                    onClick={() => setIsFullscreenImage(true)}
                  />
                  
                </div>
                
                {/* Image thumbnails */}
                {[selectedProduct.image_url, ...(selectedProduct.image_urls || [])].filter(url => url).length > 1 && (
                  <div className="flex flex-wrap gap-2 justify-center mt-4">
                    {[selectedProduct.image_url, ...(selectedProduct.image_urls || [])].filter(url => url).map((url, index) => {
                      const modalImages = [selectedProduct.image_url, ...(selectedProduct.image_urls || [])].filter(u => u);
                      const isActive = index === currentImageIndex;
                      const label = modalImages.length === 2
                        ? (index === 0 ? 'Front' : 'Back')
                        : `Photo ${index + 1}`;
                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={() => setCurrentImageIndex(index)}
                          aria-label={label}
                          aria-pressed={isActive}
                          data-testid={`modal-thumb-${index}`}
                          className="px-4 py-2 text-sm font-semibold rounded-full border-2 select-none"
                          style={{
                            backgroundColor: isActive ? primaryColor : '#ffffff',
                            borderColor: primaryColor,
                            color: isActive ? '#ffffff' : primaryColor,
                          }}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Product Details */}
              <div className="space-y-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedProduct.title}</h2>
                  {selectedProduct.grade && (
                    <span className="hbe-grade-badge mt-2 inline-block">
                      {selectedProduct.grade}
                    </span>
                  )}
                  {catalogueLine(selectedProduct) && (
                    <div className="hbe-collector-key mt-2">
                      <span className="lbl">Catalogue</span>
                      {catalogueLine(selectedProduct)}
                    </div>
                  )}
                </div>

                <div className="text-3xl font-bold">
                  {selectedProduct.price === 0 ? (
                    <span className="hbe-ink-text" data-testid="modal-contact-price">Contact For Price</span>
                  ) : (
                    <span className="hbe-ink-text">{formatDisplayPrice(selectedProduct.price)}</span>
                  )}
                </div>

                <div className="space-y-2">
                  <p className="text-gray-700 leading-relaxed">{selectedProduct.description}</p>
                  
                  {(selectedProduct.year || selectedProduct.denomination || selectedProduct.country) && (
                    <div className="grid grid-cols-1 gap-2 pt-4 border-t">
                      {selectedProduct.year && <p><span className="font-medium">Year:</span> {selectedProduct.year}</p>}
                      {selectedProduct.denomination && <p><span className="font-medium">Denomination:</span> {selectedProduct.denomination}</p>}
                      {selectedProduct.country && <p><span className="font-medium">Country:</span> {selectedProduct.country}</p>}
                    </div>
                  )}
                </div>

                {user && selectedProduct.is_available && (
                  <>
                    <Button 
                      onClick={async () => {
                        await addToCart(selectedProduct.id);
                        setSelectedProduct(null);
                      }}
                      className="w-full text-white text-lg py-3"
                      style={{ backgroundColor: (selectedProduct.is_sold || selectedProduct.is_display_only || selectedProduct.price === 0) ? '#6b7280' : primaryColor }}
                      disabled={addingToCart || selectedProduct.is_sold || selectedProduct.is_display_only || selectedProduct.price === 0}
                      data-testid="modal-add-to-cart-btn"
                    >
                      {selectedProduct.is_display_only
                        ? 'Display Only'
                        : selectedProduct.is_sold
                          ? 'SOLD'
                          : selectedProduct.price === 0
                            ? 'Contact For Price'
                            : (addingToCart ? 'Adding...' : 'Add to Cart')}
                    </Button>
                    {selectedProduct.is_display_only && (
                      <p className="text-center italic text-blue-700 text-sm" data-testid="modal-display-only-note">
                        Display only, Personal Collection
                      </p>
                    )}
                  </>
                )}

                {/* Make an Offer */}
                {!selectedProduct.is_sold && selectedProduct.allow_offers === true && (
                  <div className="pt-2 border-t">
                    {!showOfferForm ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setOfferForm({
                            name: user?.name || '',
                            email: user?.email || '',
                            amount: selectedProduct.min_offer_amount != null ? String(selectedProduct.min_offer_amount) : '',
                            message: '',
                          });
                          setShowOfferForm(true);
                        }}
                        className="w-full text-base py-3 border-2"
                        style={{ borderColor: primaryColor, color: primaryColor }}
                        data-testid="modal-make-offer-btn"
                      >
                        Make an Offer{selectedProduct.min_offer_amount != null ? ` (min $${Number(selectedProduct.min_offer_amount).toFixed(2)})` : ''}
                      </Button>
                    ) : (
                      <div className="space-y-2 rounded-lg p-3" style={{ border: `2px solid ${primaryColor}`, background: '#fffbeb' }} data-testid="offer-form">
                        <p className="text-sm font-semibold" style={{ color: primaryColor }}>Send Justin your offer</p>
                        {selectedProduct.min_offer_amount != null && (
                          <p className="text-xs" style={{ color: primaryColor }}>Minimum offer: ${Number(selectedProduct.min_offer_amount).toFixed(2)} CAD</p>
                        )}
                        <input
                          type="text"
                          placeholder="Your Name"
                          value={offerForm.name}
                          onChange={(e) => setOfferForm({ ...offerForm, name: e.target.value })}
                          className="w-full rounded-md border px-3 py-2 text-sm"
                          data-testid="offer-name-input"
                        />
                        <input
                          type="email"
                          placeholder="Your Email (Justin will reply here)"
                          value={offerForm.email}
                          onChange={(e) => setOfferForm({ ...offerForm, email: e.target.value })}
                          className="w-full rounded-md border px-3 py-2 text-sm"
                          data-testid="offer-email-input"
                        />
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="Your Offer (CAD)"
                          value={offerForm.amount}
                          onChange={(e) => setOfferForm({ ...offerForm, amount: e.target.value.replace(/[^0-9.]/g, '') })}
                          className="w-full rounded-md border px-3 py-2 pl-7 text-sm font-semibold no-spin"
                          data-testid="offer-amount-input"
                          style={{ background: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16'><text x='0' y='13' font-family='sans-serif' font-size='14' fill='%2392400e'>$</text></svg>") no-repeat 8px center, white` }}
                        />
                        <textarea
                          placeholder="Optional message (e.g. shipping to, best-by date)"
                          value={offerForm.message}
                          onChange={(e) => setOfferForm({ ...offerForm, message: e.target.value })}
                          rows={2}
                          className="w-full rounded-md border px-3 py-2 text-sm"
                          data-testid="offer-message-input"
                        />
                        <div className="flex gap-2 pt-1">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowOfferForm(false)}
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            onClick={async () => {
                              const amt = parseFloat(offerForm.amount);
                              if (!offerForm.name.trim() || !offerForm.email.trim() || !amt || amt <= 0) {
                                toast.error('Please enter your name, email, and a valid offer amount.');
                                return;
                              }
                              const minAmt = selectedProduct.min_offer_amount;
                              if (minAmt != null && amt < Number(minAmt)) {
                                toast.error(`Minimum offer for this item is $${Number(minAmt).toFixed(2)}.`);
                                return;
                              }
                              setSubmittingOffer(true);
                              try {
                                await axios.post(`${API}/offers`, {
                                  product_id: selectedProduct.id,
                                  offer_amount: amt,
                                  customer_name: offerForm.name.trim(),
                                  customer_email: offerForm.email.trim(),
                                  message: offerForm.message.trim(),
                                });
                                toast.success('Offer sent to Justin — he will reply by email.');
                                setShowOfferForm(false);
                                setOfferForm({ name: '', email: '', amount: '', message: '' });
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Could not send offer. Please try again.');
                              } finally {
                                setSubmittingOffer(false);
                              }
                            }}
                            disabled={submittingOffer}
                            className="flex-1 text-white"
                            style={{ backgroundColor: primaryColor }}
                            data-testid="offer-submit-btn"
                          >
                            {submittingOffer ? 'Sending...' : 'Send Offer'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Image Modal */}
      {isFullscreenImage && selectedProduct && (
        <FullscreenImageViewer
          images={[selectedProduct.image_url, ...(selectedProduct.image_urls || [])].filter(url => url)}
          currentIndex={currentImageIndex}
          onIndexChange={setCurrentImageIndex}
          onClose={() => setIsFullscreenImage(false)}
          rotation={selectedProduct.image_rotation || 0}
          title={selectedProduct.title}
        />
      )}
    </div>
  );
}

function CartPage() {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [siteSettings, setSiteSettings] = useState(readCachedSettings());
  const { formatDisplayPrice, displayCurrency } = useCurrency();
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    fullName: '',
    email: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    paymentMethod: 'e-transfer',
    tracking: false
  });
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    fetchCart();
    fetchSiteSettings();
  }, [user]);

  // Stripe hosted-checkout return: ?stripe=success&session_id=... or ?stripe=cancelled
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(location.search);
    const stripeState = params.get('stripe');
    const sessionId = params.get('session_id');
    if (stripeState === 'cancelled') {
      toast.error('Card payment cancelled — your cart is unchanged.');
      navigate('/cart', { replace: true });
      return;
    }
    if (stripeState === 'success' && sessionId) {
      (async () => {
        try {
          const response = await axios.post(`${API}/cart/stripe-verify`, { session_id: sessionId });
          if (response.data.paid) {
            toast.success(`Payment confirmed — order total $${response.data.total_amount} CAD. A confirmation email is on its way.`);
            fetchCart();
          } else {
            toast.error('Stripe has not confirmed this payment yet. If you completed it, give it a moment and refresh your cart.');
          }
        } catch (error) {
          toast.error('Payment verification failed: ' + (error.response?.data?.detail || 'Unknown error'));
        }
        navigate('/cart', { replace: true });
      })();
    }
  }, [user, location.search]);

  const fetchCart = async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${API}/cart`);
      setCartItems(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
      toast.error('Error loading cart');
    }
  };

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSiteSettings(response.data);
      writeCachedSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      await axios.delete(`${API}/cart/${itemId}`);
      toast.success('Item removed from cart');
      fetchCart();
    } catch (error) {
      toast.error('Error removing item from cart');
    }
  };

  const calculateTotal = () => {
    return cartItems.reduce((total, item) => {
      const discountedPrice = item.product.discount_percentage > 0 
        ? Math.max(0, item.product.price - item.product.discount_percentage)
        : item.product.price;
      // Match the backend checkout and the modal line items, which both
      // multiply by quantity (cart items are quantity 1 today, but the
      // cart-page total must not silently undercount if that changes).
      return total + discountedPrice * (item.quantity || 1);
    }, 0);
  };

  const primaryColor = siteSettings?.primary_color || HBE.bronze;
  const backgroundStyle = siteSettings ? {
    background: siteSettings.background_type === 'solid' 
      ? siteSettings.background_value
      : siteSettings.background_type === 'gradient'
      ? siteSettings.background_value
      : `url(${siteSettings.background_value}) center/cover`,
  } : {};

  if (!user) {
    return (
      <div className="min-h-screen py-8" style={backgroundStyle}>

        <div className="mx-auto px-4 md:px-6" style={{ maxWidth: HBE.contentMax }}>
          <div className="text-center py-16">
            <h1 className="text-4xl font-bold hbe-ink-text mb-4">Please Sign In</h1>
            <p className="text-xl hbe-muted-text">You need to be signed in to view your cart.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" style={backgroundStyle}>
      <div className="mx-auto px-4 md:px-6" style={{ maxWidth: HBE.contentMax }}>
        <h1 className="text-4xl font-bold hbe-ink-text mb-8 font-serif">Your Cart</h1>
        
        {cartItems.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingCart className="h-16 w-16 hbe-bronze mx-auto mb-4" />
            <h2 className="text-2xl font-bold hbe-ink-text mb-2">Your cart is empty</h2>
            <p className="hbe-muted-text mb-6">Start shopping to add items to your cart</p>
            <Button 
              onClick={() => navigate('/products')} 
              className="text-white"
              style={{ backgroundColor: primaryColor }}
            >
              Browse Products
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {cartItems.map(item => {
                const discountedPrice = item.product.discount_percentage > 0 
                  ? Math.max(0, item.product.price - item.product.discount_percentage)
                  : item.product.price;
                
                return (
                  <Card key={item.id} className="hbe-stone-border">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3 sm:gap-4">
                        <img 
                          src={thumbUrl(item.product.image_url)} 
                          alt={item.product.title}
                          className="w-24 sm:w-28 shrink-0 aspect-[3/2] object-contain rounded-lg bg-[#efe9dc] border hbe-stone-border"
                          loading="lazy"
                          decoding="async"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold hbe-ink-text leading-snug break-words">{item.product.title}</h3>
                          <p className="text-sm hbe-bronze">{item.product.category}</p>
                          {item.product.grade && (
                            <span className="hbe-grade-badge mt-1 inline-block">
                              {item.product.grade}
                            </span>
                          )}
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="flex flex-col items-end gap-2">
                            <div className="flex flex-wrap justify-end items-baseline gap-x-2">
                              {item.product.discount_percentage > 0 ? (
                                <>
                                  <span className="text-lg font-bold text-green-600">
                                    {formatDisplayPrice(discountedPrice)}
                                  </span>
                                  <span className="text-sm text-gray-500 line-through">
                                    {formatDisplayPrice(item.product.price)}
                                  </span>
                                </>
                              ) : (
                                <span className="text-lg font-bold hbe-ink-text">
                                  {formatDisplayPrice(item.product.price)}
                                </span>
                              )}
                            </div>
                            <Button 
                              onClick={() => removeFromCart(item.id)}
                              variant="outline" 
                              size="sm"
                              className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            
            <div className="lg:col-span-1">
              <Card className="hbe-stone-border sticky top-4">
                <CardHeader>
                  <CardTitle className="hbe-ink-text">Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="hbe-muted-text">Subtotal:</span>
                    <span className="font-bold hbe-ink-text">{formatDisplayPrice(calculateTotal())}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between text-lg">
                    <span className="font-bold hbe-ink-text">Total:</span>
                    <span className="font-bold hbe-ink-text">{formatDisplayPrice(calculateTotal())}</span>
                  </div>
                </CardContent>
                <CardFooter className="flex flex-col space-y-3">
                  <Button 
                    onClick={() => setShowCheckoutModal(true)}
                    className="w-full text-white"
                    style={{ backgroundColor: primaryColor }}
                  >
                    Proceed to Checkout
                  </Button>
                  <p className="text-xs hbe-bronze text-center">
                    Payment options: E-transfer, Visa, PayPal
                  </p>
                </CardFooter>
              </Card>
            </div>
          </div>
        )}
      </div>

      {/* Checkout Modal */}
      {showCheckoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowCheckoutModal(false)}
          ></div>
          
          <div className="relative bg-white rounded-lg shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl font-bold hbe-ink-text">Checkout</h2>
                <button 
                  onClick={() => setShowCheckoutModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Sub-header: instructions in serif font, size like Checkout heading */}
              <p className="text-2xl font-serif hbe-ink-text leading-snug mb-1">
                Fill out contact info and choose payment method, refer to email for invoice and contact with me, Historic.Bills@gmail.com.
              </p>
              <p className="text-2xl font-serif hbe-ink-text leading-snug mb-6">— Justin</p>

              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  if (checkoutForm.paymentMethod === 'stripe') {
                    // Stripe hosted checkout: backend creates the session from
                    // the cart, then we hand the browser to Stripe's page.
                    const response = await axios.post(`${API}/cart/stripe-checkout`, {
                      checkout_details: checkoutForm
                    });
                    window.location.href = response.data.url;
                    return;
                  }
                  const response = await axios.post(`${API}/cart/checkout`, {
                    checkout_details: checkoutForm
                  });
                  alert(`Order placed successfully!\nTotal: $${response.data.total_amount}\n\nA confirmation email has been sent to ${checkoutForm.email}. Justin will follow up to arrange payment and shipping.`);
                  setShowCheckoutModal(false);
                  fetchCart();
                } catch (error) {
                  alert('Checkout failed: ' + (error.response?.data?.detail || 'Unknown error'));
                }
              }} className="space-y-6">
                
                {/* Personal Information */}
                <div>
                  <h3 className="text-lg font-semibold hbe-ink-text mb-4">Personal Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium hbe-muted-text mb-1">Full Name</label>
                      <input
                        type="text"
                        required
                        value={checkoutForm.fullName}
                        onChange={(e) => setCheckoutForm({...checkoutForm, fullName: e.target.value})}
                        className="w-full border hbe-stone-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium hbe-muted-text mb-1">Email</label>
                      <input
                        type="email"
                        required
                        value={checkoutForm.email}
                        onChange={(e) => setCheckoutForm({...checkoutForm, email: e.target.value})}
                        className="w-full border hbe-stone-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Shipping Address */}
                <div>
                  <h3 className="text-lg font-semibold hbe-ink-text mb-4">Shipping Address</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium hbe-muted-text mb-1">Address</label>
                      <input
                        type="text"
                        required
                        value={checkoutForm.address}
                        onChange={(e) => setCheckoutForm({...checkoutForm, address: e.target.value})}
                        className="w-full border hbe-stone-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                        placeholder="123 Main Street"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium hbe-muted-text mb-1">City</label>
                        <input
                          type="text"
                          required
                          value={checkoutForm.city}
                          onChange={(e) => setCheckoutForm({...checkoutForm, city: e.target.value})}
                          className="w-full border hbe-stone-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                          placeholder="Toronto"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium hbe-muted-text mb-1">Postal Code</label>
                        <input
                          type="text"
                          required
                          value={checkoutForm.postalCode}
                          onChange={(e) => setCheckoutForm({...checkoutForm, postalCode: e.target.value})}
                          className="w-full border hbe-stone-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                          placeholder="M1A 1A1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium hbe-muted-text mb-1">Country</label>
                        <input
                          type="text"
                          required
                          value={checkoutForm.country}
                          onChange={(e) => setCheckoutForm({...checkoutForm, country: e.target.value})}
                          className="w-full border hbe-stone-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                          placeholder="Canada"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Method */}
                <div>
                  <h3 className="text-lg font-semibold hbe-ink-text mb-4">Payment Method</h3>
                  <div className="space-y-3">
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="e-transfer"
                        checked={checkoutForm.paymentMethod === 'e-transfer'}
                        onChange={(e) => setCheckoutForm({...checkoutForm, paymentMethod: e.target.value})}
                        className="hbe-bronze focus:ring-amber-500 mt-1"
                      />
                      <div>
                        <span className="hbe-muted-text">E-transfer</span>
                        <p className="text-xs hbe-muted-text mt-0.5">Send to <strong className="hbe-ink-text">historic.bills@gmail.com</strong> (auto-deposit enabled)</p>
                      </div>
                    </label>
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="stripe"
                        checked={checkoutForm.paymentMethod === 'stripe'}
                        onChange={(e) => setCheckoutForm({...checkoutForm, paymentMethod: e.target.value})}
                        className="hbe-bronze focus:ring-amber-500 mt-1"
                      />
                      <div>
                        <span className="hbe-muted-text">Credit Card — pay now with Stripe</span>
                        <p className="text-xs hbe-muted-text mt-0.5">Visa, Mastercard and other cards, on Stripe's secure checkout page. You will be taken to Stripe to pay, then brought back here.</p>
                      </div>
                    </label>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="paypal"
                        checked={checkoutForm.paymentMethod === 'paypal'}
                        onChange={(e) => setCheckoutForm({...checkoutForm, paymentMethod: e.target.value})}
                        className="hbe-bronze focus:ring-amber-500"
                      />
                      <span className="hbe-muted-text">PayPal</span>
                    </label>
                  </div>
                  {/* Payment notes */}
                  <div className="mt-3 space-y-1 text-sm hbe-muted-text">
                    <p>— All prices in Canadian Dollars <span aria-label="Canada" role="img">🇨🇦</span></p>
                    <p>— Local pickup available in Langley / Aldergrove BC</p>
                  </div>
                </div>

                {/* Shipping option */}
                <div className="border hbe-stone-border rounded-lg p-4 bg-white">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checkoutForm.tracking}
                      onChange={(e) => setCheckoutForm({ ...checkoutForm, tracking: e.target.checked })}
                      className="mt-1 h-4 w-4 hbe-bronze focus:ring-amber-500 rounded"
                      data-testid="tracking-checkbox"
                    />
                    <div>
                      <div className="font-semibold hbe-ink-text">Registered Tracking — Canada Post <span className="ml-1 hbe-muted-text font-normal">(+$12)</span></div>
                      <div className="text-xs hbe-muted-text">Recommended for shipped orders. Adds tracking + signature.</div>
                    </div>
                  </label>
                </div>

                {/* Order Summary */}
                <div className="hbe-paper rounded-lg p-4">
                  <h3 className="text-lg font-semibold hbe-ink-text mb-3">Order Summary</h3>
                  <div className="space-y-2">
                    {cartItems.map(item => (
                      <div key={item.id} className="flex justify-between text-sm">
                        <span className="hbe-muted-text">{item.product.title} (x{item.quantity})</span>
                        <span className="hbe-ink-text font-medium">
                          ${((item.product.discount_percentage > 0 
                            ? Math.max(0, item.product.price - item.product.discount_percentage) 
                            : item.product.price) * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    ))}
                    {checkoutForm.tracking && (
                      <div className="flex justify-between text-sm">
                        <span className="hbe-muted-text">Registered Tracking — Canada Post</span>
                        <span className="hbe-ink-text font-medium">$12.00</span>
                      </div>
                    )}
                    <div className="border-t hbe-stone-border pt-2 mt-2">
                      <div className="flex justify-between font-bold hbe-ink-text">
                        <span>Total:</span>
                        <span>${(calculateTotal() + (checkoutForm.tracking ? 12 : 0)).toFixed(2)} CAD</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex space-x-4">
                  <Button
                    type="button"
                    onClick={() => setShowCheckoutModal(false)}
                    className="flex-1 border hbe-stone-border hbe-muted-text bg-white hover:hbe-paper"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 text-white"
                    style={{ backgroundColor: primaryColor }}
                  >
                    Place Order
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AnnouncementBanner() {
  // Red desktop email banner removed — inquiry line lives in the scrolling TrustStrip marquee.
  return null;
}

function RequireAdmin({ children }) {
  const { user } = useAuth();
  if (!user || !user.is_admin) return <Navigate to="/" replace />;
  return children;
}

function AppShell() {
  const location = useLocation();
  const bgLocation = location.state?.backgroundLocation;
  return (
    <div className="App min-h-screen">
      <Header />
      <Routes location={bgLocation || location}>
        <Route path="/" element={<HomePage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/categories" element={<ProductsPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/product/:slug" element={<ProductDetailPage />} />
        <Route path="/counter/:offerId" element={<RequireAdmin><CounterOfferPage /></RequireAdmin>} />
        <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
      </Routes>
      {bgLocation && (
        <Routes>
          <Route path="/product/:slug" element={<ProductDetailPage asModal />} />
        </Routes>
      )}
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <CurrencyProvider>
        <BrowserRouter basename={process.env.PUBLIC_URL || ''}>
          <AppShell />
        </BrowserRouter>
      </CurrencyProvider>
    </AuthProvider>
  );
}

export default App;